import React, { useState, useEffect } from 'react';
import { 
  Lock, LogOut, Users, ClipboardList, Search, Filter, 
  CheckCircle, Clock, XCircle, AlertCircle, Trash2, 
  Eye, EyeOff, ChevronDown, ChevronUp, Building2, GraduationCap,
  Mail, Phone, MapPin, FileText, Baby, Award, Briefcase, Star,
  HelpCircle, Shield, Home, Download, FileSpreadsheet, BarChart3, Activity,
  UserPlus, Settings, Crown, Pencil, X, BookOpen, PenLine, KeyRound, Copy, Megaphone, Upload, Archive
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import CertificatesTab from '../components/admin/CertificatesTab';
import { OrganizerRegistrationsTab } from '../components/admin/OrganizerRegistrationsTab';
import { StrongBeginningsTab } from '../components/admin/StrongBeginningsTab';
import { RegistrationsHubTab } from '../components/admin/RegistrationsHubTab';
import ImpactDashboardTab from '../components/admin/impact/ImpactDashboardTab';
import HC4CCReportModal from '../components/HC4CCReportModal';
import AnalyticsDashboard from '../components/AnalyticsDashboard';

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [currentUser, setCurrentUser] = useState({ username: '', role: 'admin' });
  const [activeTab, setActiveTab] = useState('registrations');
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [boardPortalOpen, setBoardPortalOpen] = useState(false);
  const [alaaRegistrations, setAlaaRegistrations] = useState([]);
  const [itciRegistrations, setItciRegistrations] = useState([]);
  const [hc4ccInquiries, setHc4ccInquiries] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ username: '', password: '', email: '', full_name: '', role: 'admin' });
  const [showNewAdminPassword, setShowNewAdminPassword] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: '', email: '', role: 'admin' });
  const [storybookSubscriptions, setStorybookSubscriptions] = useState([]);
  const [petitionSignatures, setPetitionSignatures] = useState([]);
  const [conveningRegistrations, setConveningRegistrations] = useState([]);
  const [sendingThankYou, setSendingThankYou] = useState(false);
  const [sendingThankYouId, setSendingThankYouId] = useState(null);
  const [sendingConferenceEmail, setSendingConferenceEmail] = useState(null);
  const [sendingPostEvent, setSendingPostEvent] = useState(false);
  const [emailStats, setEmailStats] = useState(null);
  const [boardInterestSubmissions, setBoardInterestSubmissions] = useState([]);
  const [partnerInquiries, setPartnerInquiries] = useState([]);
  const [playScienceRegistrations, setPlayScienceRegistrations] = useState([]);
  const [playScienceCapacity, setPlayScienceCapacity] = useState(null);
  const [thankYouSignOns, setThankYouSignOns] = useState([]);
  const [boardVisions, setBoardVisions] = useState([]);
  const [coiSignatures, setCoiSignatures] = useState([]);
  // Action Alerts
  const [actionAlertTemplates, setActionAlertTemplates] = useState([]);
  const [selectedAlertTemplateKey, setSelectedAlertTemplateKey] = useState('');
  const [alertRecipients, setAlertRecipients] = useState('');
  const [alertSubjectOverride, setAlertSubjectOverride] = useState('');
  const [alertSendHistory, setAlertSendHistory] = useState([]);
  const [isSendingAlert, setIsSendingAlert] = useState(false);
  const [alertTestMode, setAlertTestMode] = useState(false);
  // Facility Directory
  const [facilities, setFacilities] = useState([]);
  const [facilitySearch, setFacilitySearch] = useState('');
  const [facilityWard, setFacilityWard] = useState('all');
  const [isImportingFacilities, setIsImportingFacilities] = useState(false);
  const [resetPasswordAdmin, setResetPasswordAdmin] = useState(null); // {id, username, email, full_name}
  const [resetPasswordMode, setResetPasswordMode] = useState('generate'); // 'generate' | 'custom'
  const [resetPasswordCustom, setResetPasswordCustom] = useState('');
  const [resetPasswordResult, setResetPasswordResult] = useState(null); // {new_password, email_sent}
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [isSendingForgot, setIsSendingForgot] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Check if current user is super admin
  const isSuperAdmin = currentUser.role === 'super_admin';

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/verify`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setCurrentUser({ username: data.username, role: data.role });
        localStorage.setItem('adminAuth', btoa(`${credentials.username}:${credentials.password}`));
        localStorage.setItem('adminRole', data.role);
        localStorage.setItem('adminUsername', data.username);
        toast.success(`Welcome ${data.role === 'super_admin' ? '(Super Admin)' : ''} to the Admin Dashboard`);
        fetchAllRegistrations();
      } else {
        toast.error('Invalid credentials');
      }
    } catch (error) {
      toast.error('Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentUser({ username: '', role: 'admin' });
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminUsername');
    setCredentials({ username: '', password: '' });
    toast.info('Logged out successfully');
  };

  const getAuthHeader = () => {
    const auth = localStorage.getItem('adminAuth');
    return auth ? { 'Authorization': `Basic ${auth}` } : {};
  };

  const fetchAllRegistrations = async () => {
    await Promise.all([fetchAlaaRegistrations(), fetchItciRegistrations(), fetchHc4ccInquiries()]);
  };

  const fetchAdminUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setAdminUsers(data);
      }
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.username || !newAdmin.password) {
      toast.error('Username and password are required');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        method: 'POST',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newAdmin)
      });
      
      if (response.ok) {
        toast.success('Admin user created successfully');
        setNewAdmin({ username: '', password: '', email: '', full_name: '', role: 'admin' });
        setShowAddAdmin(false);
        fetchAdminUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create admin user');
      }
    } catch (error) {
      toast.error('Failed to create admin user');
    }
  };

  const handleEditAdmin = (admin) => {
    setEditingAdmin(admin);
    setEditForm({
      full_name: admin.full_name || '',
      email: admin.email || '',
      role: admin.role || 'admin'
    });
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editingAdmin) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${editingAdmin.id}`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editForm)
      });
      
      if (response.ok) {
        toast.success('Admin user updated successfully');
        setEditingAdmin(null);
        setEditForm({ full_name: '', email: '', role: 'admin' });
        fetchAdminUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update admin user');
      }
    } catch (error) {
      toast.error('Failed to update admin user');
    }
  };

  const handleCancelEdit = () => {
    setEditingAdmin(null);
    setEditForm({ full_name: '', email: '', role: 'admin' });
  };

  const handleDeleteAdmin = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete admin "${username}"?`)) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      
      if (response.ok) {
        toast.success('Admin user deleted');
        fetchAdminUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete admin user');
      }
    } catch (error) {
      toast.error('Failed to delete admin user');
    }
  };

  const openResetPasswordModal = (admin) => {
    setResetPasswordAdmin(admin);
    setResetPasswordMode('generate');
    setResetPasswordCustom('');
    setResetPasswordResult(null);
  };

  const closeResetPasswordModal = () => {
    setResetPasswordAdmin(null);
    setResetPasswordMode('generate');
    setResetPasswordCustom('');
    setResetPasswordResult(null);
  };

  const submitResetPassword = async () => {
    if (!resetPasswordAdmin) return;
    if (resetPasswordMode === 'custom') {
      if (!resetPasswordCustom || resetPasswordCustom.length < 8) {
        toast.error('Custom password must be at least 8 characters');
        return;
      }
    }
    setIsResettingPassword(true);
    try {
      const body = resetPasswordMode === 'generate'
        ? { generate: true }
        : { password: resetPasswordCustom };
      const response = await fetch(`${API_URL}/api/admin/users/${resetPasswordAdmin.id}/reset-password`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (response.ok) {
        const data = await response.json();
        setResetPasswordResult(data);
        if (data.email_sent) {
          toast.success(`Password reset. Email sent to ${resetPasswordAdmin.email || 'user'}`);
        } else if (data.email_on_file) {
          toast.warning('Password reset, but the email failed to send. Share the password below directly.');
        } else {
          toast.warning('Password reset, but no email is on file for this user. Share the password below directly.');
        }
        fetchAdminUsers();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const submitForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotIdentifier.trim()) {
      toast.error('Please enter your username or email');
      return;
    }
    setIsSendingForgot(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: forgotIdentifier.trim() })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'If an account exists, a reset link has been sent.');
        setShowForgotPassword(false);
        setForgotIdentifier('');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSendingForgot(false);
    }
  };

  const toggleAdminStatus = async (userId, currentStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${userId}`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !currentStatus })
      });
      
      if (response.ok) {
        toast.success(`Admin ${currentStatus ? 'deactivated' : 'activated'}`);
        fetchAdminUsers();
      }
    } catch (error) {
      toast.error('Failed to update admin status');
    }
  };

  const fetchAlaaRegistrations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/alaa/registrations`, {
        headers: getAuthHeader()
      });
      if (response.status === 401) {
        handleLogout();
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setAlaaRegistrations(data);
      }
    } catch (error) {
      console.error('Error fetching ALAA registrations:', error);
    }
  };

  const fetchItciRegistrations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/itci/registrations`, {
        headers: getAuthHeader()
      });
      if (response.status === 401) {
        handleLogout();
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setItciRegistrations(data);
      }
    } catch (error) {
      console.error('Error fetching ITCI registrations:', error);
    }
  };

  const fetchHc4ccInquiries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/hc4cc/inquiries`, {
        headers: getAuthHeader()
      });
      if (response.status === 401) {
        handleLogout();
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setHc4ccInquiries(data);
      }
    } catch (error) {
      console.error('Error fetching HC4CC inquiries:', error);
    }
  };

  const updateStatus = async (registrationId, newStatus, programType) => {
    const endpoint = programType === 'alaa' ? 'alaa' : programType === 'itci' ? 'itci' : 'hc4cc';
    const path = programType === 'hc4cc' ? 'inquiries' : 'registrations';
    try {
      const response = await fetch(`${API_URL}/api/${endpoint}/${path}/${registrationId}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success(`Status updated to ${newStatus}`);
        fetchAllRegistrations();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteRegistration = async (registrationId, programType) => {
    if (!window.confirm(`Are you sure you want to delete this ${programType === 'hc4cc' ? 'inquiry' : 'registration'}?`)) return;
    const endpoint = programType === 'alaa' ? 'alaa' : programType === 'itci' ? 'itci' : 'hc4cc';
    const path = programType === 'hc4cc' ? 'inquiries' : 'registrations';
    try {
      const response = await fetch(`${API_URL}/api/${endpoint}/${path}/${registrationId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success(`${programType === 'hc4cc' ? 'Inquiry' : 'Registration'} deleted`);
        fetchAllRegistrations();
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Storybook Subscriptions
  const fetchStorybookSubscriptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/storybook/subscriptions`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setStorybookSubscriptions(data);
      }
    } catch (error) {
      console.error('Error fetching storybook subscriptions:', error);
    }
  };

  const deleteStorybookSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) return;
    try {
      const response = await fetch(`${API_URL}/api/storybook/subscriptions/${subscriptionId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success('Subscription deleted');
        fetchStorybookSubscriptions();
      } else {
        toast.error('Failed to delete subscription');
      }
    } catch (error) {
      toast.error('Failed to delete subscription');
    }
  };

  const exportStorybookSubscriptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/storybook/subscriptions/export`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        // Download as CSV
        const blob = new Blob([data.csv_data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'storybook_subscriptions.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(`Exported ${data.count} subscriptions`);
      }
    } catch (error) {
      toast.error('Failed to export subscriptions');
    }
  };

  // Petition Signatures
  const fetchPetitionSignatures = async () => {
    try {
      const response = await fetch(`${API_URL}/api/petition/signatures`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setPetitionSignatures(data);
      }
    } catch (error) {
      console.error('Error fetching petition signatures:', error);
    }
  };

  const deletePetitionSignature = async (signatureId) => {
    if (!window.confirm('Are you sure you want to delete this signature?')) return;
    try {
      const response = await fetch(`${API_URL}/api/petition/signatures/${signatureId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success('Signature deleted');
        fetchPetitionSignatures();
      } else {
        toast.error('Failed to delete signature');
      }
    } catch (error) {
      toast.error('Failed to delete signature');
    }
  };

  const exportPetitionSignatures = async () => {
    try {
      const response = await fetch(`${API_URL}/api/petition/signatures/export`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([data.csv_data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'petition_signatures.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(`Exported ${data.count} signatures`);
      }
    } catch (error) {
      toast.error('Failed to export signatures');
    }
  };

  // Convening Registrations
  const fetchConveningRegistrations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/convening/registrations`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setConveningRegistrations(data);
      }
    } catch (error) {
      console.error('Error fetching convening registrations:', error);
    }
  };

  const deleteConveningRegistration = async (registrationId) => {
    if (!window.confirm('Are you sure you want to delete this registration?')) return;
    try {
      const response = await fetch(`${API_URL}/api/convening/registrations/${registrationId}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success('Registration deleted');
        fetchConveningRegistrations();
      } else {
        toast.error('Failed to delete registration');
      }
    } catch (error) {
      toast.error('Failed to delete registration');
    }
  };

  const fetchEmailStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/convening/email-stats`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setEmailStats(data);
      }
    } catch (error) {
      console.error('Error fetching email stats:', error);
    }
  };

  const sendConferenceEmail = async (emailType) => {
    const label = emailType === '1' ? '1st Conference Email (1 Week Before)' : '2nd Conference Email (Day Before)';
    if (!window.confirm(`Send ${label} to ALL ${conveningRegistrations.length} registrants?`)) return;
    setSendingConferenceEmail(emailType);
    try {
      const response = await fetch(`${API_URL}/api/convening/send-conference-email?email_type=${emailType}`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Sent ${data.sent_count} emails${data.failed_count > 0 ? `, ${data.failed_count} failed` : ''}`);
        fetchConveningRegistrations();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send emails');
      }
    } catch {
      toast.error('Failed to send conference emails');
    } finally {
      setSendingConferenceEmail(null);
    }
  };

  const exportConveningRegistrations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/convening/registrations/export`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([data.csv_data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'convening_registrations.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(`Exported ${data.count} registrations`);
      }
    } catch (error) {
      toast.error('Failed to export registrations');
    }
  };

  const sendThankYouToAll = async () => {
    const unsent = conveningRegistrations.filter(r => !r.welcome_email_sent);
    if (unsent.length === 0) {
      toast.info('All registrants have already received the welcome email');
      return;
    }
    if (!window.confirm(`Send welcome email to ${unsent.length} registrant(s) who haven't received it yet?`)) return;
    setSendingThankYou(true);
    try {
      const response = await fetch(`${API_URL}/api/convening/send-thank-you`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ registration_ids: unsent.map(r => r.id) })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Sent ${data.sent_count} welcome emails${data.failed_count > 0 ? `, ${data.failed_count} failed` : ''}`);
        fetchConveningRegistrations();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send emails');
      }
    } catch (error) {
      toast.error('Failed to send welcome emails');
    } finally {
      setSendingThankYou(false);
    }
  };

  const sendThankYouToOne = async (registrationId, name) => {
    setSendingThankYouId(registrationId);
    try {
      const response = await fetch(`${API_URL}/api/convening/send-thank-you`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ registration_ids: [registrationId] })
      });
      if (response.ok) {
        toast.success(`Welcome email sent to ${name}`);
        fetchConveningRegistrations();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send email');
      }
    } catch (error) {
      toast.error('Failed to send welcome email');
    } finally {
      setSendingThankYouId(null);
    }
  };

  const sendPostEventToAll = async () => {
    const unsentCount = conveningRegistrations.filter(r => !r.post_event_email_sent).length;
    if (unsentCount === 0) {
      toast.info('All registrants have already received the Post-Event Thank You email. Use "Force Resend All" if you want to send it again.');
      return;
    }
    const confirmed = window.confirm(
      `Send the Post-Event Thank You email to ${unsentCount} registrant(s) who have NOT yet received it?`
    );
    if (!confirmed) return;
    setSendingPostEvent(true);
    try {
      const response = await fetch(`${API_URL}/api/convening/send-post-event`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ send_to_all: true })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Sent ${data.sent_count} thank-you emails${data.failed_count > 0 ? `, ${data.failed_count} failed` : ''}`);
        fetchConveningRegistrations();
        fetchEmailStats();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send emails');
      }
    } catch (error) {
      toast.error('Failed to send post-event emails');
    } finally {
      setSendingPostEvent(false);
    }
  };

  const forceResendPostEventToAll = async () => {
    const total = conveningRegistrations.length;
    if (total === 0) {
      toast.info('No registrants to email');
      return;
    }
    const firstConfirm = window.confirm(
      `⚠️ FORCE RESEND — this will email the Post-Event Thank You to ALL ${total} registrant(s), ` +
      `INCLUDING people who already received it. Anyone who received the previous version will ` +
      `get a SECOND copy.\n\nContinue?`
    );
    if (!firstConfirm) return;
    const typedConfirm = window.prompt(
      `To confirm, type RESEND (in uppercase) and press OK:`
    );
    if (typedConfirm !== 'RESEND') {
      toast.info('Force resend cancelled');
      return;
    }
    setSendingPostEvent(true);
    try {
      const response = await fetch(`${API_URL}/api/convening/send-post-event`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ send_to_all: true, force: true })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Force re-sent ${data.sent_count} emails${data.failed_count > 0 ? `, ${data.failed_count} failed` : ''}. Open-tracking has been reset for these recipients.`);
        fetchConveningRegistrations();
        fetchEmailStats();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to force-resend emails');
      }
    } catch (error) {
      toast.error('Failed to force-resend post-event emails');
    } finally {
      setSendingPostEvent(false);
    }
  };

  const previewPostEventEmail = () => {
    const url = `${API_URL}/api/convening/post-event-preview?name=Friend`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Board Interest Submissions
  const fetchBoardInterestSubmissions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/board-interest/submissions`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setBoardInterestSubmissions(data);
      }
    } catch (error) {
      console.error('Error fetching board interest submissions:', error);
    }
  };

  const deleteBoardInterest = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    try {
      const response = await fetch(`${API_URL}/api/board-interest/submissions/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success('Submission deleted');
        fetchBoardInterestSubmissions();
      } else {
        toast.error('Failed to delete submission');
      }
    } catch {
      toast.error('Failed to delete submission');
    }
  };

  const updateBoardInterestStatus = async (id, status) => {
    try {
      const response = await fetch(`${API_URL}/api/board-interest/submissions/${id}/status?status=${status}`, {
        method: 'PUT',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success(`Status updated to ${status}`);
        fetchBoardInterestSubmissions();
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  const exportBoardInterest = async () => {
    try {
      const response = await fetch(`${API_URL}/api/board-interest/submissions/export`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([data.csv_data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'board_interest_submissions.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(`Exported ${data.count} submissions`);
      }
    } catch {
      toast.error('Failed to export submissions');
    }
  };

  // Partner Inquiries
  const fetchPartnerInquiries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/partner-inquiry/submissions`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setPartnerInquiries(data);
      }
    } catch (error) {
      console.error('Error fetching partner inquiries:', error);
    }
  };

  const deletePartnerInquiry = async (id) => {
    if (!window.confirm('Are you sure you want to delete this partner inquiry?')) return;
    try {
      const response = await fetch(`${API_URL}/api/partner-inquiry/submissions/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success('Inquiry deleted');
        fetchPartnerInquiries();
      } else {
        toast.error('Failed to delete inquiry');
      }
    } catch {
      toast.error('Failed to delete inquiry');
    }
  };

  const updatePartnerInquiryStatus = async (id, status) => {
    try {
      const response = await fetch(`${API_URL}/api/partner-inquiry/submissions/${id}/status?status=${status}`, {
        method: 'PUT',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success(`Status updated to ${status}`);
        fetchPartnerInquiries();
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  const exportPartnerInquiries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/partner-inquiry/submissions/export`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([data.csv_data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'partner_inquiries.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(`Exported ${data.count} inquiries`);
      }
    } catch {
      toast.error('Failed to export inquiries');
    }
  };

  // Play Science Institute Registrations
  const fetchPlayScienceRegistrations = async () => {
    try {
      const [regsRes, capRes] = await Promise.all([
        fetch(`${API_URL}/api/play-science-institute/registrations`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/play-science-institute/capacity`)
      ]);
      if (regsRes.ok) {
        setPlayScienceRegistrations(await regsRes.json());
      }
      if (capRes.ok) {
        setPlayScienceCapacity(await capRes.json());
      }
    } catch (error) {
      console.error('Error fetching Play Science registrations:', error);
    }
  };

  const updatePlayScienceStatus = async (id, status) => {
    try {
      const response = await fetch(`${API_URL}/api/play-science-institute/registrations/${id}/status?status=${status}`, {
        method: 'PUT',
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.email_sent) {
          toast.success(`Status updated to ${status} and decision email sent to applicant`);
        } else if (status === 'confirmed' || status === 'waitlisted') {
          toast.success(`Status updated to ${status} (email delivery pending)`);
        } else {
          toast.success(`Status updated to ${status}`);
        }
        fetchPlayScienceRegistrations();
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  const deletePlayScienceRegistration = async (id) => {
    if (!window.confirm('Are you sure you want to delete this Play Science registration?')) return;
    try {
      const response = await fetch(`${API_URL}/api/play-science-institute/registrations/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success('Registration deleted');
        fetchPlayScienceRegistrations();
      } else {
        toast.error('Failed to delete registration');
      }
    } catch {
      toast.error('Failed to delete registration');
    }
  };

  const exportPlayScienceRegistrations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/play-science-institute/registrations/export`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([data.csv_data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'play_science_registrations.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(`Exported ${data.count} registrations`);
      }
    } catch {
      toast.error('Failed to export registrations');
    }
  };

  const downloadPlaySciencePdf = async (regId = null) => {
    try {
      const url = `${API_URL}/api/play-science-institute/registrations/export-pdf${regId ? `?reg_id=${regId}` : ''}`;
      const response = await fetch(url, { headers: getAuthHeader() });
      if (response.ok) {
        const blob = await response.blob();
        const dlUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = dlUrl;
        a.download = regId ? 'play_science_registration.pdf' : 'play_science_registrations.pdf';
        a.click();
        window.URL.revokeObjectURL(dlUrl);
        toast.success('PDF downloaded');
      } else {
        toast.error('No registrations to export');
      }
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  // FY27 Budget Win Thank-You Sign-Ons
  const fetchBoardForms = async () => {
    try {
      const [vRes, cRes] = await Promise.all([
        fetch(`${API_URL}/api/board/vision-goals`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/board/conflict-of-interest`, { headers: getAuthHeader() })
      ]);
      if (vRes.ok) setBoardVisions(await vRes.json());
      if (cRes.ok) setCoiSignatures(await cRes.json());
    } catch (error) {
      console.error('Error fetching board forms:', error);
    }
  };

  const deleteBoardForm = async (kind, id) => {
    if (!window.confirm('Delete this record permanently?')) return;
    try {
      const response = await fetch(`${API_URL}/api/board/${kind}/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success('Record deleted');
        fetchBoardForms();
      } else {
        toast.error('Failed to delete');
      }
    } catch {
      toast.error('Failed to delete');
    }
  };

  const copyBoardFormLink = (path, label) => {
    navigator.clipboard.writeText(`${window.location.origin}${path}`);
    toast.success(`${label} link copied — share it with the board`);
  };

  const fetchThankYouSignOns = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sign-ons/budget-win-thank-you/list`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setThankYouSignOns(data);
      }
    } catch (error) {
      console.error('Error fetching thank-you sign-ons:', error);
    }
  };

  const exportThankYouSignOns = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sign-ons/budget-win-thank-you/export`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([data.csv_data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'fy27_budget_win_signons.csv';
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success(`Exported ${data.count} sign-ons`);
      }
    } catch {
      toast.error('Failed to export sign-ons');
    }
  };

  // Action Alerts
  const fetchActionAlertTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/action-alerts/templates`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setActionAlertTemplates(data);
        if (data.length > 0 && !selectedAlertTemplateKey) {
          setSelectedAlertTemplateKey(data[0].key);
        }
      }
    } catch (error) {
      console.error('Error fetching alert templates:', error);
    }
  };

  const fetchActionAlertHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/action-alerts/history`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setAlertSendHistory(data);
      }
    } catch (error) {
      console.error('Error fetching alert history:', error);
    }
  };

  const [expandedAnalyticsId, setExpandedAnalyticsId] = useState(null);
  const [alertAnalytics, setAlertAnalytics] = useState({});

  const toggleAlertAnalytics = async (sendId) => {
    if (expandedAnalyticsId === sendId) {
      setExpandedAnalyticsId(null);
      return;
    }
    setExpandedAnalyticsId(sendId);
    if (!alertAnalytics[sendId]) {
      try {
        const response = await fetch(`${API_URL}/api/action-alerts/analytics/${sendId}`, {
          headers: getAuthHeader()
        });
        if (response.ok) {
          const data = await response.json();
          setAlertAnalytics(prev => ({ ...prev, [sendId]: data }));
        }
      } catch (error) {
        console.error('Error fetching alert analytics:', error);
      }
    }
  };

  const fetchFacilities = async () => {
    try {
      const response = await fetch(`${API_URL}/api/facilities`, { headers: getAuthHeader() });
      if (response.ok) setFacilities(await response.json());
    } catch (error) {
      console.error('Error fetching facilities:', error);
    }
  };

  const importFacilitiesFile = async (file) => {
    setIsImportingFacilities(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await fetch(`${API_URL}/api/facilities/import`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: fd
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Import complete — ${data.imported} added, ${data.updated} updated (${data.total_rows} rows)`);
        fetchFacilities();
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch {
      toast.error('Import failed');
    } finally {
      setIsImportingFacilities(false);
    }
  };

  const loadFacilityEmailsIntoRecipients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/facilities/emails`, { headers: getAuthHeader() });
      if (!response.ok) { toast.error('Could not load facility emails'); return; }
      const data = await response.json();
      if (data.count === 0) { toast.error('No facility emails found — import the facility report first'); return; }
      setAlertRecipients(prev => {
        const existing = prev.split(/[\s,;\n]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
        const merged = new Set([...existing, ...data.emails]);
        return Array.from(merged).join('\n');
      });
      toast.success(`Loaded ${data.count} facility emails into recipients`);
    } catch {
      toast.error('Could not load facility emails');
    }
  };

  const parseRecipientsCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/);
      const emails = new Set();
      // Try to detect an "email" column header
      let emailColIdx = -1;
      if (lines.length > 0) {
        const headers = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/^["']|["']$/g, ''));
        emailColIdx = headers.findIndex(h => h === 'email' || h === 'e-mail' || h === 'email address' || h.includes('email'));
      }
      const startRow = emailColIdx >= 0 ? 1 : 0;
      for (let i = startRow; i < lines.length; i++) {
        const cells = lines[i].split(',').map(c => c.trim().replace(/^["']|["']$/g, ''));
        const candidate = emailColIdx >= 0 ? cells[emailColIdx] : cells.find(c => c.includes('@'));
        if (candidate && candidate.includes('@')) {
          emails.add(candidate.toLowerCase());
        }
      }
      const merged = Array.from(emails).join('\n');
      setAlertRecipients(prev => {
        const existing = prev.split(/[\s,;\n]+/).map(s => s.trim()).filter(s => s.includes('@'));
        const combined = new Set([...existing.map(e => e.toLowerCase()), ...Array.from(emails)]);
        return Array.from(combined).join('\n');
      });
      toast.success(`Loaded ${emails.size} email${emails.size === 1 ? '' : 's'} from CSV${emailColIdx < 0 ? ' (no header detected — used emails from any column)' : ''}`);
      void merged;
    };
    reader.onerror = () => toast.error('Failed to read file');
    reader.readAsText(file);
  };

  const previewActionAlert = () => {
    if (!selectedAlertTemplateKey) return;
    window.open(`${API_URL}/api/action-alerts/preview/${selectedAlertTemplateKey}`, '_blank', 'noopener,noreferrer');
  };

  const sendActionAlert = async () => {
    if (!selectedAlertTemplateKey) {
      toast.error('Pick a template first');
      return;
    }
    const recipients = alertRecipients.split(/[\s,;\n]+/).map(s => s.trim()).filter(s => s.includes('@'));
    if (recipients.length === 0) {
      toast.error('No valid email addresses found. Add recipients above.');
      return;
    }
    if (!alertTestMode) {
      const ok = window.confirm(
        `Send the action alert to ${recipients.length} unique recipient(s)? This cannot be undone.`
      );
      if (!ok) return;
    }
    setIsSendingAlert(true);
    try {
      const response = await fetch(`${API_URL}/api/action-alerts/send`, {
        method: 'POST',
        headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_key: selectedAlertTemplateKey,
          recipients,
          subject: alertSubjectOverride.trim() || undefined,
          test_mode: alertTestMode
        })
      });
      if (response.ok) {
        const data = await response.json();
        if (data.test_mode) {
          toast.success(`Test send complete — delivered to the first recipient. No history recorded.`);
        } else {
          toast.success(`Sending started to ${data.queued} recipient${data.queued === 1 ? '' : 's'} — watch progress in Send History below`);
          setAlertRecipients('');
          setAlertSubjectOverride('');
          fetchActionAlertHistory();
        }
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to send action alert');
      }
    } catch {
      toast.error('Failed to send action alert');
    } finally {
      setIsSendingAlert(false);
    }
  };

  const downloadConveningPDF = (reg) => {
    const doc = `
<!DOCTYPE html>
<html>
<head>
<style>
  body { font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto; padding: 40px; color: #333; }
  .header { background: linear-gradient(135deg, #079897, #0A2037); padding: 30px; text-align: center; border-radius: 12px 12px 0 0; }
  .header h1 { color: white; margin: 0; font-size: 22px; }
  .header p { color: rgba(255,255,255,0.8); margin: 8px 0 0; font-size: 14px; }
  .content { padding: 30px; border: 1px solid #e2e8f0; border-top: none; border-radius: 0 0 12px 12px; }
  .field { margin-bottom: 16px; }
  .field-label { font-weight: bold; color: #0A2037; font-size: 13px; text-transform: uppercase; letter-spacing: 0.5px; }
  .field-value { margin-top: 4px; font-size: 15px; color: #333; }
  .section-title { font-size: 16px; font-weight: bold; color: #079897; border-bottom: 2px solid #079897; padding-bottom: 8px; margin: 24px 0 16px; }
  .event-details { background: #f0fdfa; padding: 16px; border-radius: 8px; border-left: 4px solid #079897; margin-bottom: 24px; }
  .footer { text-align: center; padding: 20px; color: #64748b; font-size: 12px; margin-top: 30px; border-top: 1px solid #e2e8f0; }
</style>
</head>
<body>
  <div class="header">
    <h1>Stronger Together: Leadership Convening</h1>
    <p>Registration Details</p>
  </div>
  <div class="content">
    <div class="event-details">
      <strong>Event:</strong> Stronger Together: Protecting Progress Through Collective Leadership<br>
      <strong>Date:</strong> Wednesday, May 27, 2026 | 9:00 AM - 3:00 PM<br>
      <strong>Location:</strong> 700 Pennsylvania Ave. S.E., Washington, DC
    </div>
    <div class="section-title">Participant Information</div>
    <div class="field"><span class="field-label">Full Name:</span><div class="field-value">${reg.first_name} ${reg.middle_name || ''} ${reg.last_name}</div></div>
    <div class="field"><span class="field-label">Organization:</span><div class="field-value">${reg.organization}</div></div>
    <div class="field"><span class="field-label">Title / Role:</span><div class="field-value">${reg.title_role}</div></div>
    <div class="field"><span class="field-label">Email:</span><div class="field-value">${reg.email}</div></div>
    <div class="field"><span class="field-label">Phone:</span><div class="field-value">${reg.phone || 'N/A'}</div></div>
    <div class="section-title">Accessibility & Logistics</div>
    <div class="field"><span class="field-label">Dietary Needs:</span><div class="field-value">${reg.dietary_needs || 'None specified'}</div></div>
    <div class="field"><span class="field-label">Accessibility Needs:</span><div class="field-value">${reg.accessibility_needs || 'None specified'}</div></div>
    <div class="field"><span class="field-label">Photo/Video Consent:</span><div class="field-value">${reg.photo_consent === 'yes' ? 'Yes, I consent' : 'No, I do not consent'}</div></div>
    <div class="section-title">Additional Input</div>
    <div class="field"><span class="field-label">Topics to Address:</span><div class="field-value">${reg.topics_to_address || 'None specified'}</div></div>
    <div class="footer">
      <strong>Status:</strong> ${reg.status.charAt(0).toUpperCase() + reg.status.slice(1)} | 
      <strong>Registered:</strong> ${new Date(reg.registered_at).toLocaleDateString()} ${new Date(reg.registered_at).toLocaleTimeString()}<br>
      DC Early Learning Collaborative
    </div>
  </div>
</body>
</html>`;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(doc);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 500);
  };

  useEffect(() => {
    const savedAuth = localStorage.getItem('adminAuth');
    const savedRole = localStorage.getItem('adminRole');
    const savedUsername = localStorage.getItem('adminUsername');
    if (savedAuth) {
      setIsAuthenticated(true);
      setCurrentUser({ 
        username: savedUsername || '', 
        role: savedRole || 'admin' 
      });
      fetchAllRegistrations();
      fetchAdminUsers();
    }
  }, []);

  // Fetch storybook subscriptions when tab changes
  useEffect(() => {
    if (isAuthenticated && activeTab === 'storybook') {
      fetchStorybookSubscriptions();
    }
  }, [isAuthenticated, activeTab]);

  // Fetch petition signatures when tab changes
  useEffect(() => {
    if (isAuthenticated && activeTab === 'petition') {
      fetchPetitionSignatures();
    }
  }, [isAuthenticated, activeTab]);

  // Fetch convening registrations when tab changes
  useEffect(() => {
    if (isAuthenticated && activeTab === 'convening') {
      fetchConveningRegistrations();
      fetchEmailStats();
    }
  }, [isAuthenticated, activeTab]);

  // Fetch board interest submissions when tab changes
  useEffect(() => {
    if (isAuthenticated && activeTab === 'board_interest') {
      fetchBoardInterestSubmissions();
    }
  }, [isAuthenticated, activeTab]);

  // Fetch partner inquiries when tab changes
  useEffect(() => {
    if (isAuthenticated && activeTab === 'partners') {
      fetchPartnerInquiries();
    }
  }, [isAuthenticated, activeTab]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'ece_pd') {
      fetchPlayScienceRegistrations();
    }
  }, [isAuthenticated, activeTab]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'signons') {
      fetchThankYouSignOns();
    }
  }, [isAuthenticated, activeTab]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'board_forms') {
      fetchBoardForms();
    }
  }, [isAuthenticated, activeTab]);

  // Fetch action alerts when tab changes
  useEffect(() => {
    if (isAuthenticated && activeTab === 'alerts') {
      fetchActionAlertTemplates();
      fetchActionAlertHistory();
      fetchNewsletterSubscribers();
    }
    if (isAuthenticated && activeTab === 'facilities') {
      fetchFacilities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeTab]);

  // Auto-refresh send history while a blast is in progress
  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'alerts') return;
    const hasActiveSend = alertSendHistory.some(h => h.status === 'sending');
    if (!hasActiveSend) return;
    const interval = setInterval(fetchActionAlertHistory, 8000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, activeTab, alertSendHistory]);

  const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);

  const fetchNewsletterSubscribers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribers`, { headers: getAuthHeader() });
      if (response.ok) setNewsletterSubscribers(await response.json());
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    }
  };

  const deleteNewsletterSubscriber = async (id) => {
    if (!window.confirm('Remove this subscriber?')) return;
    try {
      const response = await fetch(`${API_URL}/api/newsletter/subscribers/${id}`, {
        method: 'DELETE',
        headers: getAuthHeader()
      });
      if (response.ok) {
        toast.success('Subscriber removed');
        fetchNewsletterSubscribers();
      } else {
        toast.error('Failed to remove subscriber');
      }
    } catch {
      toast.error('Failed to remove subscriber');
    }
  };

  const copySubscriberEmails = () => {
    const emails = newsletterSubscribers.map((s) => s.email).join(', ');
    navigator.clipboard.writeText(emails);
    toast.success(`${newsletterSubscribers.length} subscriber email${newsletterSubscribers.length === 1 ? '' : 's'} copied — paste into the recipients box`);
  };

  const exportNewsletterSubscribers = () => {
    const rows = [['Name', 'Email', 'Subscribed At'], ...newsletterSubscribers.map((s) => [s.full_name || '', s.email, s.subscribed_at || ''])];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'newsletter_subscribers.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };


  const currentRegistrations = activeTab === 'alaa' ? alaaRegistrations : activeTab === 'itci' ? itciRegistrations : hc4ccInquiries;

  const filteredRegistrations = currentRegistrations.filter(reg => {
    const matchesSearch = 
      (reg.first_name || reg.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (reg.last_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (reg.program_name || reg.organization_name || reg.center_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || reg.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStats = (registrations) => ({
    total: registrations.length,
    pending: registrations.filter(r => r.status === 'pending').length,
    reviewing: registrations.filter(r => r.status === 'reviewing').length,
    accepted: registrations.filter(r => r.status === 'accepted').length,
    declined: registrations.filter(r => r.status === 'declined').length,
    waitlisted: registrations.filter(r => r.status === 'waitlisted').length,
    new: registrations.filter(r => r.status === 'new').length,
    in_progress: registrations.filter(r => r.status === 'in_progress').length,
    resolved: registrations.filter(r => r.status === 'resolved').length,
  });

  const stats = getStats(currentRegistrations);

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewing: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      waitlisted: 'bg-purple-100 text-purple-800',
      declined: 'bg-red-100 text-red-800',
      new: 'bg-teal-100 text-teal-800',
      in_progress: 'bg-blue-100 text-blue-800',
      resolved: 'bg-green-100 text-green-800',
      archived: 'bg-slate-100 text-slate-800'
    };
    const icons = {
      pending: Clock,
      reviewing: Eye,
      accepted: CheckCircle,
      waitlisted: AlertCircle,
      declined: XCircle,
      new: Clock,
      in_progress: Eye,
      resolved: CheckCircle,
      archived: XCircle
    };
    const Icon = icons[status] || Clock;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        <Icon className="w-3 h-3" />
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  // Export HC4CC Inquiries to CSV
  const exportHC4CCToCSV = () => {
    if (hc4ccInquiries.length === 0) {
      toast.error('No inquiries to export');
      return;
    }

    const headers = [
      'Full Name',
      'Email',
      'Phone',
      'Center/Home',
      'Ward',
      'Residence',
      'Insurance Company',
      'Coverage Type',
      'Metal Level',
      'Knows Free Services',
      'Helpful Info Requested',
      'Other Info',
      'Status',
      'Submitted Date'
    ];

    const rows = filteredRegistrations.map(inq => [
      inq.full_name || '',
      inq.email || '',
      inq.phone || '',
      inq.center_name || '',
      inq.ward || '',
      inq.residence || '',
      inq.insurance_company || '',
      inq.coverage_type || '',
      inq.metal_level || '',
      inq.knows_free_services || '',
      (inq.helpful_info || []).join('; '),
      inq.helpful_info_other || '',
      inq.status || '',
      new Date(inq.submitted_at).toLocaleDateString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `HC4CC_Inquiries_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Report exported successfully');
  };

  // Generate HC4CC Summary Report
  const generateHC4CCSummaryReport = () => {
    if (hc4ccInquiries.length === 0) {
      toast.error('No inquiries to summarize');
      return;
    }

    // Analyze the data
    const totalInquiries = filteredRegistrations.length;
    const statusCounts = {};
    const insuranceCounts = {};
    const coverageCounts = {};
    const metalCounts = {};
    const knowsServicesCounts = {};
    const helpfulInfoCounts = {};
    const wardCounts = {};
    const residenceCounts = {};

    filteredRegistrations.forEach(inq => {
      // Status
      statusCounts[inq.status] = (statusCounts[inq.status] || 0) + 1;
      // Insurance
      insuranceCounts[inq.insurance_company] = (insuranceCounts[inq.insurance_company] || 0) + 1;
      // Coverage type
      coverageCounts[inq.coverage_type] = (coverageCounts[inq.coverage_type] || 0) + 1;
      // Metal level
      metalCounts[inq.metal_level] = (metalCounts[inq.metal_level] || 0) + 1;
      // Knows services
      knowsServicesCounts[inq.knows_free_services] = (knowsServicesCounts[inq.knows_free_services] || 0) + 1;
      // Ward
      if (inq.ward) wardCounts[inq.ward] = (wardCounts[inq.ward] || 0) + 1;
      // Residence
      if (inq.residence) residenceCounts[inq.residence] = (residenceCounts[inq.residence] || 0) + 1;
      // Helpful info (multiple selection)
      (inq.helpful_info || []).forEach(info => {
        helpfulInfoCounts[info] = (helpfulInfoCounts[info] || 0) + 1;
      });
    });

    const formatCounts = (counts) => Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([key, val]) => `  ${key}: ${val} (${Math.round(val/totalInquiries*100)}%)`)
      .join('\n');

    const report = `
HC4CC INQUIRIES SUMMARY REPORT
Generated: ${new Date().toLocaleString()}
Total Inquiries: ${totalInquiries}
${statusFilter !== 'all' ? `Filter Applied: ${statusFilter}` : ''}
${searchTerm ? `Search Term: "${searchTerm}"` : ''}

========================================
STATUS BREAKDOWN
========================================
${formatCounts(statusCounts)}

========================================
INSURANCE COMPANIES
========================================
${formatCounts(insuranceCounts)}

========================================
COVERAGE TYPES
========================================
${formatCounts(coverageCounts)}

========================================
METAL LEVELS
========================================
${formatCounts(metalCounts)}

========================================
KNOWLEDGE OF FREE SERVICES
========================================
${formatCounts(knowsServicesCounts)}

========================================
INFORMATION NEEDS (Questions & Needs)
========================================
${formatCounts(helpfulInfoCounts)}

========================================
WARD DISTRIBUTION
========================================
${formatCounts(wardCounts)}

========================================
RESIDENCE
========================================
${formatCounts(residenceCounts)}

========================================
KEY INSIGHTS
========================================
• Most common insurance: ${Object.entries(insuranceCounts).sort((a,b) => b[1]-a[1])[0]?.[0] || 'N/A'}
• Most requested info: ${Object.entries(helpfulInfoCounts).sort((a,b) => b[1]-a[1])[0]?.[0] || 'N/A'}
• Most common ward: ${Object.entries(wardCounts).sort((a,b) => b[1]-a[1])[0]?.[0] || 'N/A'}
• Workers unaware of free services: ${knowsServicesCounts['No'] || 0} (${Math.round((knowsServicesCounts['No'] || 0)/totalInquiries*100)}%)
`;

    const blob = new Blob([report], { type: 'text/plain;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `HC4CC_Summary_Report_${new Date().toISOString().split('T')[0]}.txt`;
    link.click();
    toast.success('Summary report generated');
  };

  // Login Page
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800">DCELC Admin</h1>
              <p className="text-slate-500 mt-2">Sign in to access the dashboard</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Username</label>
                <Input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                  placeholder="Enter username"
                  required
                  data-testid="admin-username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={credentials.password}
                    onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                    placeholder="Enter password"
                    required
                    className="pr-10"
                    data-testid="admin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white py-6"
                data-testid="admin-login-btn"
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-slate-500 hover:text-amber-700 underline-offset-2 hover:underline transition-colors"
                  data-testid="admin-forgot-password-link"
                >
                  Forgot password?
                </button>
              </div>
            </form>
          </div>

          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" data-testid="forgot-password-modal">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center mb-3">
                      <KeyRound className="w-5 h-5 text-amber-600" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-800">Reset Your Password</h3>
                    <p className="text-sm text-slate-500 mt-1">Enter your admin username or email. If we find a matching account, we'll send a reset link.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setShowForgotPassword(false); setForgotIdentifier(''); }}
                    className="text-slate-400 hover:text-slate-600"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={submitForgotPassword} className="space-y-3">
                  <Input
                    type="text"
                    value={forgotIdentifier}
                    onChange={(e) => setForgotIdentifier(e.target.value)}
                    placeholder="username or email"
                    autoFocus
                    data-testid="forgot-password-identifier"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => { setShowForgotPassword(false); setForgotIdentifier(''); }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSendingForgot}
                      className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                      data-testid="forgot-password-submit"
                    >
                      {isSendingForgot ? 'Sending…' : 'Send Reset Link'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Program Registrations</h1>
                <p className="text-sm text-slate-500">Admin Dashboard</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="text-slate-600 border-slate-300"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Program Tabs */}
        <div className="flex flex-wrap gap-4 mb-8">
          <button
            onClick={() => { setActiveTab('impact'); setExpandedId(null); }}
            data-testid="admin-impact-tab"
            className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
              activeTab === 'impact'
                ? 'text-white shadow-lg'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
            style={activeTab === 'impact' ? { background: '#079B97' } : {}}
          >
            <BarChart3 className="w-5 h-5" />
            <div className="text-left">
              <p className="font-semibold">Impact Dashboard</p>
              <p className={`text-xs ${activeTab === 'impact' ? 'text-teal-100' : 'text-slate-400'}`}>
                Engagement &amp; outcomes
              </p>
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('analytics'); setExpandedId(null); }}
            className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
              activeTab === 'analytics'
                ? 'bg-indigo-500 text-white shadow-lg'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
          >
            <Activity className="w-5 h-5" />
            <div className="text-left">
              <p className="font-semibold">Site Analytics</p>
              <p className={`text-xs ${activeTab === 'analytics' ? 'text-indigo-100' : 'text-slate-400'}`}>
                Traffic & metrics
              </p>
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('admins'); setExpandedId(null); }}
            className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
              activeTab === 'admins'
                ? 'bg-slate-700 text-white shadow-lg'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
          >
            <Settings className="w-5 h-5" />
            <div className="text-left">
              <p className="font-semibold">Admin Users</p>
              <p className={`text-xs ${activeTab === 'admins' ? 'text-slate-300' : 'text-slate-400'}`}>
                {adminUsers.length} users
              </p>
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('convening'); setExpandedId(null); }}
            className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
              activeTab === 'convening'
                ? 'bg-cyan-700 text-white shadow-lg'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
            data-testid="admin-convening-tab"
          >
            <Users className="w-5 h-5" />
            <div className="text-left">
              <p className="font-semibold">Convening</p>
              <p className={`text-xs ${activeTab === 'convening' ? 'text-cyan-100' : 'text-slate-400'}`}>
                {conveningRegistrations.length} registrations
              </p>
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('partners'); setExpandedId(null); }}
            className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
              activeTab === 'partners'
                ? 'bg-teal-700 text-white shadow-lg'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
            data-testid="admin-partners-tab"
          >
            <Users className="w-5 h-5" />
            <div className="text-left">
              <p className="font-semibold">Partner Inquiries</p>
              <p className={`text-xs ${activeTab === 'partners' ? 'text-teal-100' : 'text-slate-400'}`}>
                {partnerInquiries.length} inquiries
              </p>
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('alerts'); setExpandedId(null); }}
            className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
              activeTab === 'alerts'
                ? 'bg-rose-700 text-white shadow-lg'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
            data-testid="admin-alerts-tab"
          >
            <Megaphone className="w-5 h-5" />
            <div className="text-left">
              <p className="font-semibold">Newsletter Alerts</p>
              <p className={`text-xs ${activeTab === 'alerts' ? 'text-rose-100' : 'text-slate-400'}`}>
                {alertSendHistory.length} sent
              </p>
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('registrations'); setExpandedId(null); }}
            className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
              activeTab === 'registrations'
                ? 'bg-slate-800 text-white shadow-lg'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
            data-testid="admin-registrations-tab"
          >
            <Users className="w-5 h-5" />
            <div className="text-left">
              <p className="font-semibold">Registrations</p>
              <p className={`text-xs ${activeTab === 'registrations' ? 'text-slate-300' : 'text-slate-400'}`}>
                All site signups
              </p>
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('strong_beginnings'); setExpandedId(null); }}
            className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
              activeTab === 'strong_beginnings'
                ? 'bg-teal-800 text-white shadow-lg'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
            data-testid="admin-strong-beginnings-tab"
          >
            <Megaphone className="w-5 h-5" />
            <div className="text-left">
              <p className="font-semibold">Strong Beginnings</p>
              <p className={`text-xs ${activeTab === 'strong_beginnings' ? 'text-teal-100' : 'text-slate-400'}`}>
                Video applications
              </p>
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('certificates'); setExpandedId(null); }}
            className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
              activeTab === 'certificates'
                ? 'bg-emerald-700 text-white shadow-lg'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
            data-testid="admin-certificates-tab"
          >
            <Award className="w-5 h-5" />
            <div className="text-left">
              <p className="font-semibold">Certificates</p>
              <p className={`text-xs ${activeTab === 'certificates' ? 'text-emerald-100' : 'text-slate-400'}`}>
                Training certificates
              </p>
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('facilities'); setExpandedId(null); }}
            className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
              activeTab === 'facilities'
                ? 'bg-sky-700 text-white shadow-lg'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
            data-testid="admin-facilities-tab"
          >
            <Building2 className="w-5 h-5" />
            <div className="text-left">
              <p className="font-semibold">Facility Directory</p>
              <p className={`text-xs ${activeTab === 'facilities' ? 'text-sky-100' : 'text-slate-400'}`}>
                Provider contacts
              </p>
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('ece_pd'); setExpandedId(null); }}
            className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
              activeTab === 'ece_pd'
                ? 'bg-emerald-700 text-white shadow-lg'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
            data-testid="admin-ece-pd-tab"
          >
            <GraduationCap className="w-5 h-5" />
            <div className="text-left">
              <p className="font-semibold">ECE Professional Development</p>
              <p className={`text-xs ${activeTab === 'ece_pd' ? 'text-emerald-100' : 'text-slate-400'}`}>
                {playScienceRegistrations.length} Play Science reg.
              </p>
            </div>
          </button>
          <button
            onClick={() => { setActiveTab('signons'); setExpandedId(null); }}
            className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
              activeTab === 'signons'
                ? 'bg-amber-600 text-white shadow-lg'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
            data-testid="admin-signons-tab"
          >
            <PenLine className="w-5 h-5" />
            <div className="text-left">
              <p className="font-semibold">FY27 Sign-Ons</p>
              <p className={`text-xs ${activeTab === 'signons' ? 'text-amber-100' : 'text-slate-400'}`}>
                {thankYouSignOns.length} {thankYouSignOns.length === 1 ? 'signature' : 'signatures'}
              </p>
            </div>
          </button>
        </div>

        {/* Board Portal & Archive Sections */}
        <div className="mb-8 -mt-4">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => { setBoardPortalOpen(!boardPortalOpen); }}
              data-testid="admin-board-portal-toggle"
              className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all ${
                boardPortalOpen || ['board_interest', 'board_forms'].includes(activeTab)
                  ? 'bg-[#0a1628] text-white shadow-lg'
                  : 'bg-white text-slate-500 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              <Briefcase className="w-4 h-4" />
              Board Portal
              {boardPortalOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setArchiveOpen(!archiveOpen)}
              data-testid="admin-archive-toggle"
              className={`flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold transition-all ${
                archiveOpen || ['alaa', 'itci', 'hc4cc', 'storybook', 'petition'].includes(activeTab)
                  ? 'bg-slate-700 text-white shadow-lg'
                  : 'bg-white text-slate-500 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              <Archive className="w-4 h-4" />
              Archive
              {archiveOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
          {boardPortalOpen && (
            <div className="flex flex-wrap gap-4 mt-4 p-4 bg-slate-100/70 border border-slate-200 rounded-xl" data-testid="admin-board-portal-section">
              <button
                onClick={() => { setActiveTab('board_interest'); setExpandedId(null); }}
                data-testid="admin-board-interest-tab"
                className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
                  activeTab === 'board_interest'
                    ? 'bg-emerald-700 text-white shadow-lg'
                    : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                }`}
              >
                <UserPlus className="w-5 h-5" />
                <div className="text-left">
                  <p className="font-semibold">Board Interest</p>
                  <p className={`text-xs ${activeTab === 'board_interest' ? 'text-emerald-100' : 'text-slate-400'}`}>
                    {boardInterestSubmissions.length} submissions
                  </p>
                </div>
              </button>
              <button
                onClick={() => { setActiveTab('board_forms'); setExpandedId(null); }}
                data-testid="admin-board-forms-tab"
                className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
                  activeTab === 'board_forms'
                    ? 'bg-[#0a1628] text-white shadow-lg'
                    : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                }`}
              >
                <ClipboardList className="w-5 h-5" />
                <div className="text-left">
                  <p className="font-semibold">Board Forms</p>
                  <p className={`text-xs ${activeTab === 'board_forms' ? 'text-slate-300' : 'text-slate-400'}`}>
                    Vision &amp; COI
                  </p>
                </div>
              </button>
            </div>
          )}
          {archiveOpen && (
            <div className="flex flex-wrap gap-4 mt-4 p-4 bg-slate-100/70 border border-slate-200 rounded-xl" data-testid="admin-archive-section">
              <button
                onClick={() => { setActiveTab('alaa'); setExpandedId(null); }}
                data-testid="admin-alaa-tab"
                className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
                  activeTab === 'alaa'
                    ? 'bg-amber-500 text-white shadow-lg'
                    : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                }`}
              >
                <Award className="w-5 h-5" />
                <div className="text-left">
                  <p className="font-semibold">Emerging Leaders</p>
                  <p className={`text-xs ${activeTab === 'alaa' ? 'text-amber-100' : 'text-slate-400'}`}>
                    {alaaRegistrations.length} applications
                  </p>
                </div>
              </button>
              <button
                onClick={() => { setActiveTab('itci'); setExpandedId(null); }}
                data-testid="admin-itci-tab"
                className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
                  activeTab === 'itci'
                    ? 'bg-rose-500 text-white shadow-lg'
                    : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                }`}
              >
                <Baby className="w-5 h-5" />
                <div className="text-left">
                  <p className="font-semibold">Infant Toddler Institute</p>
                  <p className={`text-xs ${activeTab === 'itci' ? 'text-rose-100' : 'text-slate-400'}`}>
                    {itciRegistrations.length} applications
                  </p>
                </div>
              </button>
              <button
                onClick={() => { setActiveTab('hc4cc'); setExpandedId(null); }}
                data-testid="admin-hc4cc-tab"
                className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
                  activeTab === 'hc4cc'
                    ? 'bg-teal-500 text-white shadow-lg'
                    : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                }`}
              >
                <Shield className="w-5 h-5" />
                <div className="text-left">
                  <p className="font-semibold">HC4CC Inquiries</p>
                  <p className={`text-xs ${activeTab === 'hc4cc' ? 'text-teal-100' : 'text-slate-400'}`}>
                    {hc4ccInquiries.length} inquiries
                  </p>
                </div>
              </button>
              <button
                onClick={() => { setActiveTab('storybook'); setExpandedId(null); }}
                data-testid="admin-storybook-tab"
                className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
                  activeTab === 'storybook'
                    ? 'bg-green-600 text-white shadow-lg'
                    : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                }`}
              >
                <BookOpen className="w-5 h-5" />
                <div className="text-left">
                  <p className="font-semibold">Storybook Subs</p>
                  <p className={`text-xs ${activeTab === 'storybook' ? 'text-green-100' : 'text-slate-400'}`}>
                    {storybookSubscriptions.length} subscribers
                  </p>
                </div>
              </button>
              <button
                onClick={() => { setActiveTab('petition'); setExpandedId(null); }}
                data-testid="admin-petition-tab"
                className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all ${
                  activeTab === 'petition'
                    ? 'bg-teal-600 text-white shadow-lg'
                    : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
                }`}
              >
                <PenLine className="w-5 h-5" />
                <div className="text-left">
                  <p className="font-semibold">Petition</p>
                  <p className={`text-xs ${activeTab === 'petition' ? 'text-teal-100' : 'text-slate-400'}`}>
                    {petitionSignatures.length} signatures
                  </p>
                </div>
              </button>
            </div>
          )}
        </div>

        {/* Analytics Tab Content */}
        {activeTab === 'impact' ? (
          <ImpactDashboardTab API_URL={API_URL} getAuthHeader={getAuthHeader} />
        ) : activeTab === 'analytics' ? (
          <AnalyticsDashboard isSuperAdmin={isSuperAdmin} />
        ) : activeTab === 'convening' ? (
          /* Convening Registrations Tab */
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Convening Registrations</h2>
                <p className="text-sm text-slate-500">
                  Registrations for "Stronger Together: Leadership Convening" - May 27, 2026
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button 
                  onClick={sendThankYouToAll}
                  disabled={sendingThankYou || conveningRegistrations.length === 0}
                  className="bg-cyan-700 hover:bg-cyan-800 text-white"
                  data-testid="admin-convening-send-all"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {sendingThankYou ? 'Sending...' : `Send Welcome Email (${conveningRegistrations.filter(r => !r.welcome_email_sent).length} unsent)`}
                </Button>
                <Button
                  onClick={() => sendConferenceEmail('1')}
                  disabled={sendingConferenceEmail === '1' || conveningRegistrations.length === 0}
                  variant="outline"
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  data-testid="admin-convening-conf-email-1"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {sendingConferenceEmail === '1' ? 'Sending...' : '1st Reminder (1 Week)'}
                </Button>
                <Button
                  onClick={() => sendConferenceEmail('2')}
                  disabled={sendingConferenceEmail === '2' || conveningRegistrations.length === 0}
                  variant="outline"
                  className="text-rose-700 border-rose-300 hover:bg-rose-50"
                  data-testid="admin-convening-conf-email-2"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {sendingConferenceEmail === '2' ? 'Sending...' : '2nd Reminder (Day Before)'}
                </Button>
                <Button
                  onClick={previewPostEventEmail}
                  variant="outline"
                  className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                  data-testid="admin-convening-post-event-preview"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Preview Post-Event Email
                </Button>
                <Button
                  onClick={sendPostEventToAll}
                  disabled={sendingPostEvent || conveningRegistrations.length === 0}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="admin-convening-send-post-event"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {sendingPostEvent ? 'Sending...' : `Post-Event Thank You (${conveningRegistrations.filter(r => !r.post_event_email_sent).length} unsent)`}
                </Button>
                <Button
                  onClick={forceResendPostEventToAll}
                  disabled={sendingPostEvent || conveningRegistrations.length === 0}
                  variant="outline"
                  className="text-red-700 border-red-300 hover:bg-red-50"
                  data-testid="admin-convening-force-resend-post-event"
                  title="Resend the Post-Event Thank You email to EVERY registrant, including those who already received it. Use this when you need accurate open-tracking after a broken send."
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {sendingPostEvent ? 'Sending...' : `Force Resend All (${conveningRegistrations.length})`}
                </Button>
                <Button 
                  onClick={exportConveningRegistrations}
                  variant="outline"
                  className="text-cyan-700 border-cyan-200 hover:bg-cyan-50"
                  data-testid="admin-convening-export"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-slate-800">{conveningRegistrations.length}</p>
                <p className="text-sm text-slate-500">Total Registered</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-green-600">{conveningRegistrations.filter(r => r.welcome_email_sent).length}</p>
                <p className="text-sm text-slate-500">Welcome Email Sent</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-red-500">{conveningRegistrations.filter(r => !r.welcome_email_sent).length}</p>
                <p className="text-sm text-slate-500">Email Not Sent</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-teal-600">{conveningRegistrations.filter(r => r.photo_consent === 'yes').length}</p>
                <p className="text-sm text-slate-500">Photo Consent</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-amber-600">{conveningRegistrations.filter(r => r.dietary_needs).length}</p>
                <p className="text-sm text-slate-500">Dietary Needs</p>
              </div>
            </div>

            {/* Email Tracking Dashboard */}
            {emailStats && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <p className="text-sm text-slate-600 font-medium">Email Open Tracking</p>
                  <Button size="sm" variant="outline" onClick={fetchEmailStats} className="text-xs">
                    Refresh
                  </Button>
                </div>
                <div className="p-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-cyan-50 rounded-lg p-4 border border-cyan-200">
                      <p className="text-sm font-medium text-cyan-800 mb-2">Welcome Email</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-lg font-bold text-cyan-700">{emailStats.welcome_email.opened}/{emailStats.welcome_email.sent}</p>
                          <p className="text-xs text-cyan-600">opened / sent</p>
                        </div>
                        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-cyan-100">
                          <span className="text-sm font-bold text-cyan-700">
                            {emailStats.welcome_email.sent > 0 ? Math.round((emailStats.welcome_email.opened / emailStats.welcome_email.sent) * 100) : 0}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                      <p className="text-sm font-medium text-amber-800 mb-2">1st Reminder</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-lg font-bold text-amber-700">{emailStats.conference_email_1.opened}/{emailStats.conference_email_1.sent}</p>
                          <p className="text-xs text-amber-600">opened / sent</p>
                        </div>
                        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-amber-100">
                          <span className="text-sm font-bold text-amber-700">
                            {emailStats.conference_email_1.sent > 0 ? Math.round((emailStats.conference_email_1.opened / emailStats.conference_email_1.sent) * 100) : 0}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-rose-50 rounded-lg p-4 border border-rose-200">
                      <p className="text-sm font-medium text-rose-800 mb-2">2nd Reminder</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-lg font-bold text-rose-700">{emailStats.conference_email_2.opened}/{emailStats.conference_email_2.sent}</p>
                          <p className="text-xs text-rose-600">opened / sent</p>
                        </div>
                        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-rose-100">
                          <span className="text-sm font-bold text-rose-700">
                            {emailStats.conference_email_2.sent > 0 ? Math.round((emailStats.conference_email_2.opened / emailStats.conference_email_2.sent) * 100) : 0}%
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                      <p className="text-sm font-medium text-emerald-800 mb-2">Post-Event Thank You</p>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-lg font-bold text-emerald-700">{(emailStats.post_event_email?.opened ?? 0)}/{(emailStats.post_event_email?.sent ?? 0)}</p>
                          <p className="text-xs text-emerald-600">opened / sent</p>
                        </div>
                        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-emerald-100">
                          <span className="text-sm font-bold text-emerald-700">
                            {emailStats.post_event_email?.sent > 0 ? Math.round((emailStats.post_event_email.opened / emailStats.post_event_email.sent) * 100) : 0}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Per-registrant tracking table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-2 px-3 text-slate-600 font-medium">Name</th>
                          <th className="text-left py-2 px-3 text-slate-600 font-medium">Email</th>
                          <th className="text-center py-2 px-3 text-cyan-700 font-medium">Welcome</th>
                          <th className="text-center py-2 px-3 text-amber-700 font-medium">1st Reminder</th>
                          <th className="text-center py-2 px-3 text-rose-700 font-medium">2nd Reminder</th>
                          <th className="text-center py-2 px-3 text-emerald-700 font-medium">Post-Event</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailStats.registrations.map((r) => (
                          <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2 px-3 font-medium text-slate-800">{r.name}</td>
                            <td className="py-2 px-3 text-slate-500">{r.email}</td>
                            <td className="py-2 px-3 text-center">
                              {!r.welcome_sent ? (
                                <span className="text-xs text-slate-400">Not sent</span>
                              ) : r.welcome_opened ? (
                                <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Opened</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">Sent</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {!r.conf1_sent ? (
                                <span className="text-xs text-slate-400">Not sent</span>
                              ) : r.conf1_opened ? (
                                <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Opened</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">Sent</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {!r.conf2_sent ? (
                                <span className="text-xs text-slate-400">Not sent</span>
                              ) : r.conf2_opened ? (
                                <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Opened</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">Sent</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {!r.post_event_sent ? (
                                <span className="text-xs text-slate-400">Not sent</span>
                              ) : r.post_event_opened ? (
                                <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">Opened</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">Sent</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Registrations List */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 bg-slate-50 border-b border-slate-200">
                <p className="text-sm text-slate-600 font-medium">All Registrations</p>
              </div>
              
              {conveningRegistrations.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No registrations yet</p>
                  <p className="text-sm mt-1">Registrations will appear here when people register for the convening</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {conveningRegistrations.map((reg) => (
                    <div key={reg.id} className="p-4 hover:bg-slate-50">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-cyan-100">
                            <Users className="w-5 h-5 text-cyan-700" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">
                              {reg.first_name} {reg.middle_name || ''} {reg.last_name}
                            </p>
                            <p className="text-sm text-slate-500">{reg.email}</p>
                            <p className="text-sm text-slate-600 mt-1">
                              <span className="font-medium">{reg.title_role}</span> at {reg.organization}
                            </p>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                reg.welcome_email_sent
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {reg.welcome_email_sent ? `Welcome Sent` : 'Welcome Not Sent'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                reg.conference_email_1_sent
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-slate-100 text-slate-500'
                              }`}>
                                {reg.conference_email_1_sent ? '1st Reminder Sent' : '1st Reminder Pending'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                reg.conference_email_2_sent
                                  ? 'bg-rose-100 text-rose-700'
                                  : 'bg-slate-100 text-slate-500'
                              }`}>
                                {reg.conference_email_2_sent ? '2nd Reminder Sent' : '2nd Reminder Pending'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                reg.post_event_email_sent
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-slate-100 text-slate-500'
                              }`}>
                                {reg.post_event_email_sent ? 'Post-Event Sent' : 'Post-Event Pending'}
                              </span>
                              {reg.phone && (
                                <span className="text-xs text-slate-400 flex items-center gap-1">
                                  <Phone className="w-3 h-3" /> {reg.phone}
                                </span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                reg.photo_consent === 'yes' 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                Photo: {reg.photo_consent === 'yes' ? 'Consented' : 'Declined'}
                              </span>
                              {reg.dietary_needs && (
                                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                  Diet: {reg.dietary_needs}
                                </span>
                              )}
                              {reg.accessibility_needs && (
                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                  Access: {reg.accessibility_needs}
                                </span>
                              )}
                            </div>
                            {reg.topics_to_address && (
                              <p className="text-sm text-slate-600 mt-2 italic bg-slate-50 p-2 rounded">
                                "{reg.topics_to_address}"
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400">
                            {new Date(reg.registered_at).toLocaleDateString()}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-cyan-700 border-cyan-200 hover:bg-cyan-50"
                            onClick={() => sendThankYouToOne(reg.id, `${reg.first_name} ${reg.last_name}`)}
                            disabled={sendingThankYouId === reg.id}
                            title="Send Thank You Letter"
                            data-testid={`convening-send-${reg.id}`}
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-cyan-700 border-cyan-200 hover:bg-cyan-50"
                            onClick={() => downloadConveningPDF(reg)}
                            title="Download PDF"
                            data-testid={`convening-pdf-${reg.id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => deleteConveningRegistration(reg.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'petition' ? (
          /* Petition Signatures Tab */
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Petition Signatures</h2>
                <p className="text-sm text-slate-500">
                  Signatures for "Protect Early Childhood Education in DC"
                </p>
              </div>
              <Button 
                onClick={exportPetitionSignatures}
                variant="outline"
                className="text-teal-600 border-teal-200 hover:bg-teal-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-slate-800">{petitionSignatures.length}</p>
                <p className="text-sm text-slate-500">Total Signatures</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-teal-600">{petitionSignatures.filter(s => s.is_dc_resident).length}</p>
                <p className="text-sm text-slate-500">DC Residents</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-blue-600">{petitionSignatures.filter(s => s.share_publicly).length}</p>
                <p className="text-sm text-slate-500">Public Names</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-purple-600">{petitionSignatures.filter(s => s.comment).length}</p>
                <p className="text-sm text-slate-500">With Comments</p>
              </div>
            </div>

            {/* Signatures List */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 bg-slate-50 border-b border-slate-200">
                <p className="text-sm text-slate-600 font-medium">All Signatures</p>
              </div>
              
              {petitionSignatures.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <PenLine className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No signatures yet</p>
                  <p className="text-sm mt-1">Signatures will appear here when people sign the petition</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {petitionSignatures.map((sig) => (
                    <div key={sig.id} className="p-4 hover:bg-slate-50">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-teal-100">
                            <PenLine className="w-5 h-5 text-teal-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">
                              {sig.first_name} {sig.last_name}
                            </p>
                            <p className="text-sm text-slate-500">{sig.email}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {sig.zip_code && (
                                <span className="text-xs text-slate-400">ZIP: {sig.zip_code}</span>
                              )}
                              {sig.is_dc_resident && (
                                <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">DC Resident</span>
                              )}
                              {sig.share_publicly && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Public</span>
                              )}
                            </div>
                            {sig.comment && (
                              <p className="text-sm text-slate-600 mt-2 italic bg-slate-50 p-2 rounded">
                                "{sig.comment}"
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-slate-400">
                            {new Date(sig.signed_at).toLocaleDateString()}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => deletePetitionSignature(sig.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'storybook' ? (
          /* Storybook Subscriptions Tab */
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Storybook Subscriptions</h2>
                <p className="text-sm text-slate-500">
                  People interested in receiving the Educator Storybook
                </p>
              </div>
              <Button 
                onClick={exportStorybookSubscriptions}
                variant="outline"
                className="text-green-600 border-green-200 hover:bg-green-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-slate-800">{storybookSubscriptions.length}</p>
                <p className="text-sm text-slate-500">Total Subscribers</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-green-600">{storybookSubscriptions.filter(s => s.is_active).length}</p>
                <p className="text-sm text-slate-500">Active</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-slate-400">{storybookSubscriptions.filter(s => !s.is_active).length}</p>
                <p className="text-sm text-slate-500">Unsubscribed</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-blue-600">
                  {storybookSubscriptions.filter(s => s.role === 'Early Childhood Educator').length}
                </p>
                <p className="text-sm text-slate-500">Educators</p>
              </div>
            </div>

            {/* Subscriptions List */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 bg-slate-50 border-b border-slate-200">
                <p className="text-sm text-slate-600 font-medium">All Subscriptions</p>
              </div>
              
              {storybookSubscriptions.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No subscriptions yet</p>
                  <p className="text-sm mt-1">Subscribers will appear here when people sign up</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {storybookSubscriptions.map((sub) => (
                    <div key={sub.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          sub.is_active ? 'bg-green-100' : 'bg-slate-100'
                        }`}>
                          <Mail className={`w-5 h-5 ${sub.is_active ? 'text-green-600' : 'text-slate-400'}`} />
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">
                            {sub.first_name || sub.last_name ? `${sub.first_name || ''} ${sub.last_name || ''}`.trim() : 'No name provided'}
                          </p>
                          <p className="text-sm text-slate-500">{sub.email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {sub.organization && (
                              <span className="text-xs text-slate-400">{sub.organization}</span>
                            )}
                            {sub.role && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{sub.role}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          sub.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {sub.is_active ? 'Active' : 'Unsubscribed'}
                        </span>
                        <span className="text-xs text-slate-400">
                          {new Date(sub.subscribed_at).toLocaleDateString()}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => deleteStorybookSubscription(sub.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'board_interest' ? (
          /* Board Interest Tab */
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Board Interest Submissions</h2>
                <p className="text-sm text-slate-500">Interest forms for the DCELC Board of Directors</p>
              </div>
              <Button
                onClick={exportBoardInterest}
                variant="outline"
                className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                data-testid="admin-board-interest-export"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-slate-800">{boardInterestSubmissions.length}</p>
                <p className="text-sm text-slate-500">Total Submissions</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-blue-600">{boardInterestSubmissions.filter(s => s.status === 'new').length}</p>
                <p className="text-sm text-slate-500">New</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-amber-600">{boardInterestSubmissions.filter(s => s.status === 'reviewed').length}</p>
                <p className="text-sm text-slate-500">Reviewed</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-green-600">{boardInterestSubmissions.filter(s => s.status === 'contacted').length}</p>
                <p className="text-sm text-slate-500">Contacted</p>
              </div>
            </div>

            {/* Submissions List */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {boardInterestSubmissions.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <UserPlus className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No board interest submissions yet</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {boardInterestSubmissions.map((sub) => (
                    <div key={sub.id} className="p-4 hover:bg-slate-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-emerald-100 shrink-0">
                            <UserPlus className="w-5 h-5 text-emerald-700" />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-slate-800">{sub.first_name} {sub.last_name}</p>
                            <p className="text-sm text-slate-500">{sub.email}</p>
                            <p className="text-sm text-slate-600 mt-1">
                              <span className="font-medium">{sub.title_role}</span> at {sub.organization}
                            </p>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                sub.status === 'new' ? 'bg-blue-100 text-blue-700' :
                                sub.status === 'reviewed' ? 'bg-amber-100 text-amber-700' :
                                sub.status === 'contacted' ? 'bg-green-100 text-green-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                              </span>
                              {sub.ward && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{sub.ward}</span>}
                              {sub.years_ece && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{sub.years_ece}</span>}
                              {sub.hours_per_month && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{sub.hours_per_month}</span>}
                            </div>
                            {sub.sectors.length > 0 && (
                              <p className="text-xs text-slate-500 mt-2">
                                <span className="font-medium">Sectors:</span> {sub.sectors.join(', ')}
                              </p>
                            )}
                            {sub.focus_areas.length > 0 && (
                              <p className="text-xs text-slate-500 mt-1">
                                <span className="font-medium">Focus:</span> {sub.focus_areas.join(', ')}
                              </p>
                            )}
                            {expandedId === sub.id && (
                              <div className="mt-3 space-y-2 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                                <p><strong>Bio:</strong> {sub.professional_bio}</p>
                                <p><strong>Why Board:</strong> {sub.why_board}</p>
                                <p><strong>Contribution:</strong> {sub.contribution}</p>
                                {sub.skills.length > 0 && <p><strong>Skills:</strong> {sub.skills.join(', ')}</p>}
                                {sub.committees.length > 0 && <p><strong>Committees:</strong> {sub.committees.join(', ')}</p>}
                                {sub.fundraising_comfort && <p><strong>Fundraising Comfort:</strong> {sub.fundraising_comfort}/5</p>}
                                {sub.board_history && <p><strong>Board History:</strong> {sub.board_history}</p>}
                                {sub.networks && <p><strong>Networks:</strong> {sub.networks}</p>}
                                {sub.referral && <p><strong>Referral:</strong> {sub.referral}{sub.referral_name ? ` (${sub.referral_name})` : ''}</p>}
                                {sub.resume_link && <p><strong>LinkedIn:</strong> <a href={sub.resume_link} target="_blank" rel="noreferrer" className="text-emerald-600 underline">{sub.resume_link}</a></p>}
                                {sub.additional_info && <p><strong>Additional:</strong> {sub.additional_info}</p>}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-slate-400">{new Date(sub.submitted_at).toLocaleDateString()}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setExpandedId(expandedId === sub.id ? null : sub.id)}
                            className="text-slate-600"
                          >
                            {expandedId === sub.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                          <select
                            value={sub.status}
                            onChange={(e) => updateBoardInterestStatus(sub.id, e.target.value)}
                            className="text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                          >
                            <option value="new">New</option>
                            <option value="reviewed">Reviewed</option>
                            <option value="contacted">Contacted</option>
                            <option value="accepted">Accepted</option>
                            <option value="declined">Declined</option>
                          </select>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => deleteBoardInterest(sub.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'partners' ? (
          /* Partner Inquiries Tab */
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Partner Inquiries</h2>
                <p className="text-sm text-slate-500">Submissions from the public "Partner With Us" form</p>
              </div>
              <Button
                onClick={exportPartnerInquiries}
                variant="outline"
                className="text-teal-700 border-teal-200 hover:bg-teal-50"
                data-testid="admin-partners-export"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-slate-800">{partnerInquiries.length}</p>
                <p className="text-sm text-slate-500">Total Inquiries</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-blue-600">{partnerInquiries.filter(s => s.status === 'new').length}</p>
                <p className="text-sm text-slate-500">New</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-amber-600">{partnerInquiries.filter(s => s.status === 'reviewed').length}</p>
                <p className="text-sm text-slate-500">Reviewed</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-green-600">{partnerInquiries.filter(s => s.follow_up === 'yes').length}</p>
                <p className="text-sm text-slate-500">Wants Follow-up</p>
              </div>
            </div>

            {/* Inquiries List */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {partnerInquiries.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No partner inquiries yet</p>
                  <p className="text-sm mt-1">Submissions from the Partner With Us form will appear here.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {partnerInquiries.map((sub) => (
                    <div key={sub.id} className="p-4 hover:bg-slate-50">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-teal-100 shrink-0">
                            <Users className="w-5 h-5 text-teal-700" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-800">{sub.full_name}</p>
                            <p className="text-sm text-slate-500">{sub.email} {sub.phone ? `• ${sub.phone}` : ''}</p>
                            <p className="text-sm text-slate-600 mt-1">
                              <span className="font-medium">{sub.title_role}</span> at {sub.organization}
                            </p>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                sub.status === 'new' ? 'bg-blue-100 text-blue-700' :
                                sub.status === 'reviewed' ? 'bg-amber-100 text-amber-700' :
                                sub.status === 'contacted' ? 'bg-green-100 text-green-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                              </span>
                              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{sub.org_type}</span>
                              {sub.follow_up === 'yes' && (
                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Wants Follow-up</span>
                              )}
                              {sub.website && (
                                <a href={sub.website.startsWith('http') ? sub.website : `https://${sub.website}`} target="_blank" rel="noreferrer" className="text-xs text-teal-700 underline">
                                  {sub.website}
                                </a>
                              )}
                            </div>
                            {sub.partnership_interests.length > 0 && (
                              <p className="text-xs text-slate-500 mt-2">
                                <span className="font-medium">Interests:</span> {sub.partnership_interests.join(', ')}
                                {sub.partnership_interest_other ? ` • Other: ${sub.partnership_interest_other}` : ''}
                              </p>
                            )}
                            {expandedId === sub.id && (
                              <div className="mt-3 space-y-2 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                                <p><strong>Mission:</strong> {sub.mission}</p>
                                <p><strong>Communities Served:</strong> {sub.communities_served}</p>
                                {sub.partnership_interest_other && (
                                  <p><strong>Other Interest:</strong> {sub.partnership_interest_other}</p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-slate-400">{new Date(sub.submitted_at).toLocaleDateString()}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setExpandedId(expandedId === sub.id ? null : sub.id)}
                            className="text-slate-600"
                            data-testid={`admin-partner-expand-${sub.id}`}
                          >
                            {expandedId === sub.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                          <select
                            value={sub.status}
                            onChange={(e) => updatePartnerInquiryStatus(sub.id, e.target.value)}
                            className="text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                            data-testid={`admin-partner-status-${sub.id}`}
                          >
                            <option value="new">New</option>
                            <option value="reviewed">Reviewed</option>
                            <option value="contacted">Contacted</option>
                            <option value="active">Active Partner</option>
                            <option value="declined">Declined</option>
                          </select>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => deletePartnerInquiry(sub.id)}
                            data-testid={`admin-partner-delete-${sub.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'alerts' ? (
          /* Newsletter / Action Alerts Tab */
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Newsletter / Action Alerts</h2>
                <p className="text-sm text-slate-500">Send branded campaign-style emails to a custom recipient list.</p>
              </div>
            </div>

            {/* Newsletter subscribers */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="admin-newsletter-subscribers">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h3 className="font-bold text-slate-800">Newsletter Subscribers ({newsletterSubscribers.length})</h3>
                  <p className="text-xs text-slate-500">Sign-ups from the "Subscribe to Our Monthly Newsletter" box on the newsletter pages.</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="text-teal-700 border-teal-200 hover:bg-teal-50" onClick={copySubscriberEmails} disabled={newsletterSubscribers.length === 0} data-testid="admin-copy-subscriber-emails">
                    <Copy className="w-4 h-4 mr-1" /> Copy All Emails
                  </Button>
                  <Button size="sm" variant="outline" className="text-slate-700 border-slate-200 hover:bg-slate-50" onClick={exportNewsletterSubscribers} disabled={newsletterSubscribers.length === 0} data-testid="admin-export-subscribers">
                    <Download className="w-4 h-4 mr-1" /> Export CSV
                  </Button>
                </div>
              </div>
              {newsletterSubscribers.length === 0 ? (
                <div className="p-6 text-center text-slate-500 text-sm">No subscribers yet — share the newsletter page to grow the list.</div>
              ) : (
                <div className="overflow-x-auto max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-600 uppercase tracking-wider">Name</th>
                        <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-600 uppercase tracking-wider">Email</th>
                        <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-600 uppercase tracking-wider">Subscribed</th>
                        <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {newsletterSubscribers.map((s, idx) => (
                        <tr key={s.id} className="hover:bg-slate-50" data-testid={`admin-subscriber-row-${idx}`}>
                          <td className="px-4 py-2.5 text-slate-800">{s.full_name || '—'}</td>
                          <td className="px-4 py-2.5 text-slate-600">{s.email}</td>
                          <td className="px-4 py-2.5 text-slate-400 text-xs">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : '—'}</td>
                          <td className="px-4 py-2.5">
                            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => deleteNewsletterSubscriber(s.id)} aria-label={`Remove subscriber ${s.email}`} data-testid={`admin-subscriber-delete-${idx}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
              {/* Template */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Template</label>
                <select
                  value={selectedAlertTemplateKey}
                  onChange={(e) => setSelectedAlertTemplateKey(e.target.value)}
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
                  data-testid="alert-template-select"
                >
                  {actionAlertTemplates.length === 0 && <option>Loading…</option>}
                  {(() => {
                    const groups = {};
                    actionAlertTemplates.forEach(t => {
                      const cat = t.category || 'Action Alert';
                      if (!groups[cat]) groups[cat] = [];
                      groups[cat].push(t);
                    });
                    // Stable category order
                    const order = ['News Alert', 'Announcement', 'Action Alert'];
                    const sorted = Object.keys(groups).sort((a, b) => {
                      const ai = order.indexOf(a);
                      const bi = order.indexOf(b);
                      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                    });
                    const labels = { 'News Alert': '📰 News Alerts', 'Announcement': '📣 Announcements', 'Action Alert': '⚠️ Action Alerts' };
                    return sorted.map(cat => (
                      <optgroup key={cat} label={labels[cat] || cat}>
                        {groups[cat].map(t => (
                          <option key={t.key} value={t.key}>{t.name}</option>
                        ))}
                      </optgroup>
                    ));
                  })()}
                </select>
                {actionAlertTemplates.length > 0 && (() => {
                  const t = actionAlertTemplates.find(x => x.key === selectedAlertTemplateKey);
                  if (!t) return null;
                  const cat = t.category || 'Action Alert';
                  const badgeStyles = {
                    'News Alert': 'bg-blue-100 text-blue-800',
                    'Announcement': 'bg-amber-100 text-amber-800',
                    'Action Alert': 'bg-rose-100 text-rose-700'
                  };
                  const badgeLabels = { 'News Alert': '📰 News Alert', 'Announcement': '📣 Announcement', 'Action Alert': '⚠️ Action Alert' };
                  return (
                    <div className="mt-2 flex items-center flex-wrap gap-2">
                      <span className={`text-[10px] font-bold tracking-widest uppercase px-2 py-1 rounded ${badgeStyles[cat] || badgeStyles['Action Alert']}`}>
                        {badgeLabels[cat] || cat}
                      </span>
                      <span className="text-xs text-slate-500">
                        Subject: <span className="font-medium text-slate-700">{t.subject}</span>
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Subject override */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Subject override <span className="text-slate-400 font-normal">(optional)</span></label>
                <Input
                  value={alertSubjectOverride}
                  onChange={(e) => setAlertSubjectOverride(e.target.value)}
                  placeholder="Leave blank to use the template's default subject"
                  data-testid="alert-subject-override"
                />
              </div>

              {/* Recipients */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-semibold text-slate-600">
                    Recipients <span className="text-slate-400 font-normal">(one email per line, commas, or semicolons)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={loadFacilityEmailsIntoRecipients}
                      data-testid="alert-load-facility-emails"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-sky-200 bg-sky-50 hover:border-sky-400 text-xs font-semibold text-sky-700"
                    >
                      <Building2 className="w-3.5 h-3.5" />
                      Load Facility Emails
                    </button>
                    <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 hover:border-rose-300 cursor-pointer text-xs font-medium text-slate-600">
                      <Upload className="w-3.5 h-3.5" />
                      Upload CSV
                      <input
                        type="file"
                        accept=".csv,text/csv"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) parseRecipientsCSV(f);
                          e.target.value = '';
                        }}
                        data-testid="alert-csv-upload"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setAlertRecipients('')}
                      className="text-xs text-slate-500 hover:text-rose-600 underline-offset-2 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <textarea
                  value={alertRecipients}
                  onChange={(e) => setAlertRecipients(e.target.value)}
                  rows={8}
                  placeholder="recipient1@example.com&#10;recipient2@example.com&#10;recipient3@example.com"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-rose-500"
                  data-testid="alert-recipients-textarea"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {(() => {
                    const list = alertRecipients.split(/[\s,;\n]+/).map(s => s.trim()).filter(s => s.includes('@'));
                    const unique = new Set(list.map(e => e.toLowerCase()));
                    return `${unique.size} unique valid email${unique.size === 1 ? '' : 's'} detected`;
                  })()}
                </p>
              </div>

              {/* Test mode + actions */}
              <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-slate-100">
                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="alert-test-mode-toggle">
                  <input
                    type="checkbox"
                    checked={alertTestMode}
                    onChange={(e) => setAlertTestMode(e.target.checked)}
                    className="accent-rose-600"
                  />
                  <span><strong>Test mode</strong> — only send to the first recipient, don&apos;t record in history</span>
                </label>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={previewActionAlert}
                    disabled={!selectedAlertTemplateKey}
                    className="text-slate-700"
                    data-testid="alert-preview-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Email
                  </Button>
                  <Button
                    onClick={sendActionAlert}
                    disabled={isSendingAlert}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid="alert-send-btn"
                  >
                    <Megaphone className="w-4 h-4 mr-2" />
                    {isSendingAlert ? 'Sending…' : (alertTestMode ? 'Send Test' : 'Send Action Alert')}
                  </Button>
                </div>
              </div>
            </div>

            {/* History */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-3.5 border-b border-slate-200">
                <h3 className="text-base font-semibold text-slate-800">Send History</h3>
                <p className="text-xs text-slate-500">Past action-alert blasts, most recent first.</p>
              </div>
              {alertSendHistory.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Megaphone className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">No alerts sent yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                      <tr>
                        <th className="text-left py-2 px-4">Sent</th>
                        <th className="text-left py-2 px-4">Template</th>
                        <th className="text-left py-2 px-4">Subject</th>
                        <th className="text-center py-2 px-4">Sent</th>
                        <th className="text-center py-2 px-4">Failed</th>
                        <th className="text-center py-2 px-4">Opens</th>
                        <th className="text-center py-2 px-4">Clicks</th>
                        <th className="text-left py-2 px-4">By</th>
                        <th className="text-center py-2 px-4">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alertSendHistory.map((h) => (
                        <React.Fragment key={h.id}>
                        <tr className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2.5 px-4 text-slate-600 whitespace-nowrap">{new Date(h.sent_at).toLocaleString()}</td>
                          <td className="py-2.5 px-4 text-slate-800 font-medium">
                            {h.template_name}
                            {h.status === 'sending' && (
                              <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-100 text-amber-800" data-testid={`alert-sending-badge-${h.id}`}>
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                Sending {h.sent_count + h.failed_count}/{h.total_recipients}
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 px-4 text-slate-600 max-w-xs truncate">{h.subject}</td>
                          <td className="py-2.5 px-4 text-center font-semibold text-emerald-700">{h.sent_count}</td>
                          <td className="py-2.5 px-4 text-center font-semibold text-rose-600">{h.failed_count}</td>
                          <td className="py-2.5 px-4 text-center whitespace-nowrap" data-testid={`alert-opens-${h.id}`}>
                            <span className="font-semibold text-sky-700">{h.unique_opens ?? 0}</span>
                            <span className="text-xs text-slate-400 ml-1">({h.open_rate ?? 0}%)</span>
                          </td>
                          <td className="py-2.5 px-4 text-center whitespace-nowrap" data-testid={`alert-clicks-${h.id}`}>
                            <span className="font-semibold text-violet-700">{h.unique_clicks ?? 0}</span>
                            <span className="text-xs text-slate-400 ml-1">({h.click_rate ?? 0}%)</span>
                          </td>
                          <td className="py-2.5 px-4 text-slate-500">{h.sent_by}</td>
                          <td className="py-2.5 px-4 text-center">
                            <button
                              type="button"
                              onClick={() => toggleAlertAnalytics(h.id)}
                              data-testid={`alert-analytics-toggle-${h.id}`}
                              aria-expanded={expandedAnalyticsId === h.id}
                              aria-label={`${expandedAnalyticsId === h.id ? 'Hide' : 'Show'} recipient analytics for ${h.template_name}`}
                              className="text-xs font-semibold text-teal-700 hover:text-teal-900 underline underline-offset-2 focus:outline-none focus:ring-2 focus:ring-teal-500 rounded px-1 py-0.5"
                            >
                              {expandedAnalyticsId === h.id ? 'Hide' : 'View'}
                            </button>
                          </td>
                        </tr>
                        {expandedAnalyticsId === h.id && (
                          <tr className="border-b border-slate-100 bg-slate-50/60">
                            <td colSpan={9} className="px-4 py-3">
                              {!alertAnalytics[h.id] ? (
                                <p className="text-xs text-slate-500">Loading recipient analytics…</p>
                              ) : alertAnalytics[h.id].recipients.length === 0 ? (
                                <p className="text-xs text-slate-500">No per-recipient tracking data for this send (sent before analytics were enabled).</p>
                              ) : (
                                <div className="overflow-x-auto" data-testid={`alert-analytics-detail-${h.id}`}>
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-slate-500 uppercase">
                                        <th className="text-left py-1.5 px-3">Recipient</th>
                                        <th className="text-center py-1.5 px-3">Opens</th>
                                        <th className="text-center py-1.5 px-3">Clicks</th>
                                        <th className="text-left py-1.5 px-3">First Opened</th>
                                        <th className="text-left py-1.5 px-3">Links Clicked</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {alertAnalytics[h.id].recipients.map((r) => (
                                        <tr key={r.email} className="border-t border-slate-200">
                                          <td className="py-1.5 px-3 text-slate-700 font-medium">{r.email}</td>
                                          <td className="py-1.5 px-3 text-center text-sky-700 font-semibold">{r.opens}</td>
                                          <td className="py-1.5 px-3 text-center text-violet-700 font-semibold">{r.clicks}</td>
                                          <td className="py-1.5 px-3 text-slate-500 whitespace-nowrap">{r.first_open ? new Date(r.first_open).toLocaleString() : '—'}</td>
                                          <td className="py-1.5 px-3 text-slate-500 max-w-md truncate">{r.clicked_urls.length > 0 ? r.clicked_urls.join(', ') : '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'certificates' ? (
          <CertificatesTab API_URL={API_URL} getAuthHeader={getAuthHeader} />
        ) : activeTab === 'registrations' ? (
          <RegistrationsHubTab API_URL={API_URL} getAuthHeader={getAuthHeader} />
        ) : activeTab === 'strong_beginnings' ? (
          <StrongBeginningsTab API_URL={API_URL} getAuthHeader={getAuthHeader} />
        ) : activeTab === 'facilities' ? (
          /* Facility Contact Directory Tab */
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Facility Contact Directory</h2>
                <p className="text-sm text-slate-500">OSSE Child Development Facility Report — source list for news alerts, newsletters &amp; other communications</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold cursor-pointer ${isImportingFacilities ? 'opacity-60 pointer-events-none' : ''}`}>
                  <Upload className="w-4 h-4" />
                  {isImportingFacilities ? 'Importing…' : 'Import / Refresh (.xlsx)'}
                  <input
                    type="file"
                    accept=".xlsx,.xlsm"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) importFacilitiesFile(f);
                      e.target.value = '';
                    }}
                    data-testid="facility-import-input"
                  />
                </label>
                <button
                  onClick={() => {
                    const emails = Array.from(new Set(facilities.map(f => f.email).filter(Boolean)));
                    navigator.clipboard.writeText(emails.join('\n'));
                    toast.success(`Copied ${emails.length} unique emails`);
                  }}
                  disabled={facilities.length === 0}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 hover:border-sky-400 text-sm font-medium text-slate-700 disabled:opacity-50"
                  data-testid="facility-copy-emails"
                >
                  <Copy className="w-4 h-4" />
                  Copy All Emails
                </button>
                <button
                  onClick={() => {
                    const header = 'License Number,Facility Name,License Type,Address,Ward,Point of Contact,Email,Phone';
                    const rows = facilities.map(f => [f.license_number, f.facility_name, f.license_type, f.address, f.ward, f.point_of_contact, f.email, f.phone].map(v => `"${(v || '').replace(/"/g, '""')}"`).join(','));
                    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = 'facility_contacts.csv'; a.click();
                    URL.revokeObjectURL(url);
                  }}
                  disabled={facilities.length === 0}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 hover:border-sky-400 text-sm font-medium text-slate-700 disabled:opacity-50"
                  data-testid="facility-export-csv"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </div>
            </div>

            {/* Stats + filters */}
            <div className="flex items-center gap-3 flex-wrap">
              <div className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-sm">
                <span className="font-bold text-sky-700" data-testid="facility-total-count">{facilities.length}</span> <span className="text-slate-500">facilities</span>
                <span className="mx-2 text-slate-300">|</span>
                <span className="font-bold text-sky-700">{new Set(facilities.map(f => f.email).filter(Boolean)).size}</span> <span className="text-slate-500">unique emails</span>
              </div>
              <div className="relative flex-1 min-w-[220px] max-w-sm">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
                <input
                  type="text"
                  value={facilitySearch}
                  onChange={(e) => setFacilitySearch(e.target.value)}
                  placeholder="Search name, contact, email…"
                  aria-label="Search facilities"
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  data-testid="facility-search-input"
                />
              </div>
              <select
                value={facilityWard}
                onChange={(e) => setFacilityWard(e.target.value)}
                aria-label="Filter by ward"
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 bg-white"
                data-testid="facility-ward-filter"
              >
                <option value="all">All Wards</option>
                {[...new Set(facilities.map(f => f.ward).filter(Boolean))].sort().map(w => (
                  <option key={w} value={w}>Ward {w}</option>
                ))}
              </select>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {facilities.length === 0 ? (
                <div className="p-10 text-center text-slate-500">
                  <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">No facilities yet. Import the OSSE Child Development Facility Report (.xlsx) to build the directory.</p>
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-4">Facility</th>
                        <th className="text-left py-2 px-4">Ward</th>
                        <th className="text-left py-2 px-4">Type</th>
                        <th className="text-left py-2 px-4">Contact</th>
                        <th className="text-left py-2 px-4">Email</th>
                        <th className="text-left py-2 px-4">Phone</th>
                      </tr>
                    </thead>
                    <tbody data-testid="facility-table-body">
                      {facilities
                        .filter(f => facilityWard === 'all' || f.ward === facilityWard)
                        .filter(f => {
                          const q = facilitySearch.toLowerCase();
                          if (!q) return true;
                          return [f.facility_name, f.point_of_contact, f.email, f.license_number, f.address].some(v => (v || '').toLowerCase().includes(q));
                        })
                        .map((f) => (
                          <tr key={f.id} className="border-b border-slate-100 hover:bg-sky-50/40">
                            <td className="py-2.5 px-4">
                              <p className="font-medium text-slate-800">{f.facility_name}</p>
                              <p className="text-xs text-slate-400">{f.license_number}</p>
                            </td>
                            <td className="py-2.5 px-4 text-slate-600">{f.ward}</td>
                            <td className="py-2.5 px-4 text-slate-600 whitespace-nowrap">{f.license_type}</td>
                            <td className="py-2.5 px-4 text-slate-600">{f.point_of_contact || '—'}</td>
                            <td className="py-2.5 px-4">
                              {f.email ? (
                                <a href={`mailto:${f.email}`} className="text-sky-700 hover:underline">{f.email}</a>
                              ) : <span className="text-slate-400">—</span>}
                            </td>
                            <td className="py-2.5 px-4 text-slate-600 whitespace-nowrap">{f.phone || '—'}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'ece_pd' ? (
          /* ECE Professional Development Tab (Play Science Institute + future trainings) */
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">ECE Professional Development</h2>
                <p className="text-sm text-slate-500">Training registrations & applications</p>
              </div>
            </div>

            {/* Organizer Training Series registrations */}
            <OrganizerRegistrationsTab API_URL={API_URL} getAuthHeader={getAuthHeader} />

            {/* Play Science Institute Section */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-5 bg-gradient-to-r from-teal-700 to-emerald-700 text-white flex items-center justify-between flex-wrap gap-3">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.22em] text-amber-300 mb-1">
                    Play Science Institute
                  </p>
                  <h3 className="text-lg font-bold">Registrations &mdash; Saturday, Sept 19, 2026</h3>
                  <p className="text-white/70 text-xs mt-0.5">700 Pennsylvania Ave SE &bull; 8 AM &ndash; 5 PM</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    onClick={exportPlayScienceRegistrations}
                    variant="outline"
                    className="text-white border-white/30 hover:bg-white/10 bg-transparent"
                    data-testid="admin-play-science-export"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button
                    onClick={() => downloadPlaySciencePdf()}
                    variant="outline"
                    className="text-white border-white/30 hover:bg-white/10 bg-transparent"
                    data-testid="admin-play-science-export-pdf"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Export PDF
                  </Button>
                </div>
              </div>

              {/* Seat Capacity Bar */}
              {playScienceCapacity && (
                <div className="px-6 py-4 bg-slate-50 border-b border-slate-200" data-testid="admin-play-science-capacity">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Seats</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                        playScienceCapacity.is_full
                          ? 'bg-red-100 text-red-700'
                          : playScienceCapacity.available <= 5
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {playScienceCapacity.is_full ? 'FULL — Waitlist active' : `${playScienceCapacity.available} available`}
                      </span>
                    </div>
                    <span className="text-sm font-bold text-slate-700">
                      {playScienceCapacity.occupied} / {playScienceCapacity.cap}
                    </span>
                  </div>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        playScienceCapacity.is_full
                          ? 'bg-red-500'
                          : playScienceCapacity.available <= 5
                          ? 'bg-amber-500'
                          : 'bg-gradient-to-r from-teal-500 to-emerald-600'
                      }`}
                      style={{ width: `${Math.min(100, (playScienceCapacity.occupied / playScienceCapacity.cap) * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 border-b border-slate-200">
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-slate-800">{playScienceRegistrations.length}</p>
                  <p className="text-sm text-slate-500">Total Applications</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-blue-600">
                    {playScienceRegistrations.filter(r => r.status === 'new').length}
                  </p>
                  <p className="text-sm text-slate-500">New</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-green-600">
                    {playScienceRegistrations.filter(r => r.status === 'confirmed').length}
                  </p>
                  <p className="text-sm text-slate-500">Confirmed</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-2xl font-bold text-amber-600">
                    {playScienceRegistrations.filter(r => r.status === 'waitlisted').length}
                  </p>
                  <p className="text-sm text-slate-500">Waitlisted</p>
                </div>
              </div>

              {/* Registration List */}
              {playScienceRegistrations.length === 0 ? (
                <div className="p-10 text-center text-slate-500">
                  <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No Play Science Institute applications yet</p>
                  <p className="text-sm mt-1">Submissions will appear here as educators apply.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {playScienceRegistrations.map((reg) => (
                    <div key={reg.id} className="p-4 hover:bg-slate-50" data-testid={`admin-play-science-row-${reg.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-emerald-100 shrink-0">
                            <GraduationCap className="w-5 h-5 text-emerald-700" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-semibold text-slate-800">{reg.full_name}</p>
                            <p className="text-sm text-slate-500">{reg.email} {reg.phone ? `• ${reg.phone}` : ''}</p>
                            <p className="text-sm text-slate-600 mt-1">
                              <span className="font-medium">{reg.current_role}</span>
                              {reg.age_range ? ` • Age range: ${reg.age_range}` : ''}
                            </p>
                            <p className="text-sm text-slate-600 mt-0.5">
                              <span className="text-slate-500">Program:</span> {reg.program_setting}
                            </p>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                reg.status === 'new' ? 'bg-blue-100 text-blue-700' :
                                reg.status === 'contacted' ? 'bg-amber-100 text-amber-700' :
                                reg.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                                reg.status === 'waitlisted' ? 'bg-orange-100 text-orange-700' :
                                reg.status === 'withdrawn' ? 'bg-slate-100 text-slate-600' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {reg.status.charAt(0).toUpperCase() + reg.status.slice(1)}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                reg.program_in_dc === 'yes' ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {reg.program_in_dc === 'yes' ? 'DC-based' : 'Outside DC'}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                reg.can_attend_full_day === 'yes' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                Full-day: {reg.can_attend_full_day.toUpperCase()}
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                reg.can_attend_orientation === 'yes' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                Orientation: {reg.can_attend_orientation.toUpperCase()}
                              </span>
                              {reg.reuse_depot_interest === 'yes' && (
                                <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                                  Reuse Depot ({reg.reuse_depot_transport === 'yes' ? 'needs transport' : 'no transport'})
                                </span>
                              )}
                            </div>
                            {expandedId === reg.id && (
                              <div className="mt-4 space-y-3 text-sm text-slate-700 bg-slate-50 p-4 rounded-lg border border-slate-200">
                                <div>
                                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Why this training matters</p>
                                  <p className="whitespace-pre-wrap">{reg.training_matters_why}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">What play means in their practice</p>
                                  <p className="whitespace-pre-wrap">{reg.play_meaning}</p>
                                </div>
                                {reg.attendance_barriers && (
                                  <div>
                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Attendance barriers</p>
                                    <p>{reg.attendance_barriers}</p>
                                  </div>
                                )}
                                {reg.accessibility_needs && (
                                  <div>
                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Accessibility needs</p>
                                    <p>{reg.accessibility_needs}</p>
                                  </div>
                                )}
                                {reg.dietary_restrictions && (
                                  <div>
                                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Dietary restrictions</p>
                                    <p>{reg.dietary_restrictions}</p>
                                  </div>
                                )}
                                <div>
                                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">How they heard about the Institute</p>
                                  <p>{reg.how_heard}</p>
                                </div>
                                <div className="flex gap-2 text-xs text-slate-500 pt-2 border-t border-slate-200">
                                  <span>Understood commitment: <strong>{reg.understand_seat_commitment.toUpperCase()}</strong></span>
                                  <span>&bull;</span>
                                  <span>Acknowledgment: <strong>{reg.acknowledgment_accepted ? 'ACCEPTED' : 'NOT ACCEPTED'}</strong></span>
                                </div>
                              </div>
                            )}
                            {/* Quick action buttons — send decision email */}
                            <div className="flex items-center gap-2 mt-3 flex-wrap">
                              {reg.status !== 'confirmed' && (
                                <Button
                                  size="sm"
                                  onClick={() => updatePlayScienceStatus(reg.id, 'confirmed')}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                                  data-testid={`admin-play-science-accept-${reg.id}`}
                                >
                                  <CheckCircle className="w-3.5 h-3.5 mr-1" />
                                  Accept &amp; Send Email
                                </Button>
                              )}
                              {reg.status !== 'waitlisted' && (
                                <Button
                                  size="sm"
                                  onClick={() => updatePlayScienceStatus(reg.id, 'waitlisted')}
                                  variant="outline"
                                  className="text-amber-700 border-amber-300 hover:bg-amber-50 text-xs h-8"
                                  data-testid={`admin-play-science-waitlist-${reg.id}`}
                                >
                                  <Clock className="w-3.5 h-3.5 mr-1" />
                                  Waitlist &amp; Send Email
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-slate-400">{new Date(reg.submitted_at).toLocaleDateString()}</span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setExpandedId(expandedId === reg.id ? null : reg.id)}
                            className="text-slate-600"
                            data-testid={`admin-play-science-expand-${reg.id}`}
                          >
                            {expandedId === reg.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                          <select
                            value={reg.status}
                            onChange={(e) => updatePlayScienceStatus(reg.id, e.target.value)}
                            className="text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                            data-testid={`admin-play-science-status-${reg.id}`}
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="waitlisted">Waitlisted</option>
                            <option value="withdrawn">Withdrawn</option>
                          </select>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-teal-700 border-teal-200 hover:bg-teal-50"
                            onClick={() => downloadPlaySciencePdf(reg.id)}
                            title="Download application as PDF"
                            data-testid={`admin-play-science-pdf-${reg.id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                            onClick={() => deletePlayScienceRegistration(reg.id)}
                            data-testid={`admin-play-science-delete-${reg.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Placeholder for future trainings */}
            <div className="bg-slate-100 rounded-xl border-2 border-dashed border-slate-300 p-6 text-center">
              <p className="text-sm text-slate-500">
                <strong className="text-slate-700">Future Trainings</strong> &mdash; new professional development
                offerings will appear here as they launch.
              </p>
            </div>
          </div>
        ) : activeTab === 'signons' ? (
          /* FY27 Budget Win Thank-You Sign-Ons Tab */
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">FY27 Budget Win Sign-Ons</h2>
                <p className="text-sm text-slate-500">
                  Signatures on DCELC&#39;s official thank-you letter to the DC Council and Chairman Mendelson.
                </p>
              </div>
              <Button
                onClick={exportThankYouSignOns}
                variant="outline"
                className="text-amber-700 border-amber-200 hover:bg-amber-50"
                data-testid="admin-signons-export"
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-slate-800">{thankYouSignOns.length}</p>
                <p className="text-sm text-slate-500">Total Sign-Ons</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-amber-600">
                  {new Set(thankYouSignOns.map(s => (s.organization || '').toLowerCase().trim()).filter(Boolean)).size}
                </p>
                <p className="text-sm text-slate-500">Unique Organizations</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <p className="text-2xl font-bold text-teal-600">
                  {thankYouSignOns.filter(s => s.title).length}
                </p>
                <p className="text-sm text-slate-500">With Titles</p>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {thankYouSignOns.length === 0 ? (
                <div className="p-10 text-center text-slate-500">
                  <PenLine className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No sign-ons yet</p>
                  <p className="text-sm mt-1">Signatures from the FY27 Budget Win thank-you letter will appear here.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">#</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Name</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Organization</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Title</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Email</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Signed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {thankYouSignOns.map((signon, idx) => (
                        <tr key={signon.id || idx} className="hover:bg-amber-50/40" data-testid={`admin-signon-row-${idx}`}>
                          <td className="px-4 py-3 text-slate-400 text-xs">{thankYouSignOns.length - idx}</td>
                          <td className="px-4 py-3 font-semibold text-slate-800">{signon.full_name}</td>
                          <td className="px-4 py-3 text-slate-700">{signon.organization}</td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{signon.title || '—'}</td>
                          <td className="px-4 py-3 text-slate-500 text-xs">
                            <a href={`mailto:${signon.email}`} className="hover:text-teal-700 underline">{signon.email}</a>
                          </td>
                          <td className="px-4 py-3 text-slate-400 text-xs">
                            {signon.signed_at ? new Date(signon.signed_at).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'board_forms' ? (
          /* Board Forms Tab: Vision & Goals + Conflict of Interest */
          <div className="space-y-8">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Board Forms</h2>
                <p className="text-sm text-slate-500">
                  Share the links below with board members. Completed forms are captured here automatically and a
                  notification is emailed to admin@dcelc.org.
                </p>
              </div>
            </div>

            {/* Share links */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800">Vision &amp; Goals Form</p>
                  <p className="text-xs text-slate-500 truncate" title={`${window.location.origin}/board/vision-goals`}>{window.location.origin}/board/vision-goals</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-teal-700 border-teal-200 hover:bg-teal-50 shrink-0"
                  onClick={() => copyBoardFormLink('/board/vision-goals', 'Vision & Goals')}
                  data-testid="admin-copy-vision-link"
                >
                  <Copy className="w-4 h-4 mr-1" /> Copy Link
                </Button>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800">Conflict of Interest (e-sign)</p>
                  <p className="text-xs text-slate-500 truncate" title={`${window.location.origin}/board/conflict-of-interest`}>{window.location.origin}/board/conflict-of-interest</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-teal-700 border-teal-200 hover:bg-teal-50 shrink-0"
                  onClick={() => copyBoardFormLink('/board/conflict-of-interest', 'Conflict of Interest')}
                  data-testid="admin-copy-coi-link"
                >
                  <Copy className="w-4 h-4 mr-1" /> Copy Link
                </Button>
              </div>
            </div>

            {/* Vision & Goals submissions */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-bold text-slate-800">Vision &amp; Goals Responses ({boardVisions.length})</h3>
              </div>
              {boardVisions.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">No responses yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Board Member</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Term</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Committees</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Submitted</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {boardVisions.map((v, idx) => (
                        <tr key={v.id} className="hover:bg-slate-50" data-testid={`admin-vision-row-${idx}`}>
                          <td className="px-4 py-3 font-semibold text-slate-800">{v.board_member_name}</td>
                          <td className="px-4 py-3 text-slate-600 text-xs">{v.term || '—'}</td>
                          <td className="px-4 py-3 text-slate-600 text-xs">{v.committees || '—'}</td>
                          <td className="px-4 py-3 text-slate-400 text-xs">{v.submitted_at ? new Date(v.submitted_at).toLocaleDateString() : '—'}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <a href={`${API_URL}/api/board/vision-goals/${v.id}/pdf`} download>
                                <Button size="sm" variant="outline" className="text-teal-700 border-teal-200 hover:bg-teal-50" title="Download completed form as PDF" data-testid={`admin-vision-pdf-${idx}`}>
                                  <FileText className="w-4 h-4" />
                                </Button>
                              </a>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => deleteBoardForm('vision-goals', v.id)} aria-label={`Delete response from ${v.board_member_name}`} data-testid={`admin-vision-delete-${idx}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* COI signatures */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="font-bold text-slate-800">Signed Conflict of Interest Certificates ({coiSignatures.length})</h3>
              </div>
              {coiSignatures.length === 0 ? (
                <div className="p-8 text-center text-slate-500 text-sm">No signed certificates yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Printed Name</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Position</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Signed Date</th>
                        <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {coiSignatures.map((c, idx) => (
                        <tr key={c.id} className="hover:bg-slate-50" data-testid={`admin-coi-row-${idx}`}>
                          <td className="px-4 py-3 font-semibold text-slate-800">{c.printed_name}</td>
                          <td className="px-4 py-3 text-slate-600 text-xs">{c.position}</td>
                          <td className="px-4 py-3 text-slate-400 text-xs">{c.signed_date}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <a href={`${API_URL}/api/board/conflict-of-interest/${c.id}/pdf`} download>
                                <Button size="sm" variant="outline" className="text-teal-700 border-teal-200 hover:bg-teal-50" title="Download signed certificate as PDF" data-testid={`admin-coi-pdf-${idx}`}>
                                  <FileText className="w-4 h-4" />
                                </Button>
                              </a>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => deleteBoardForm('conflict-of-interest', c.id)} aria-label={`Delete certificate from ${c.printed_name}`} data-testid={`admin-coi-delete-${idx}`}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'admins' ? (
          /* Admin Management Tab */
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Admin Users</h2>
                <p className="text-sm text-slate-500">
                  {isSuperAdmin ? 'Manage admin access to the dashboard' : 'View admin users (read-only)'}
                </p>
              </div>
              {isSuperAdmin && (
                <Button 
                  onClick={() => setShowAddAdmin(!showAddAdmin)}
                  className="bg-slate-700 hover:bg-slate-800"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Admin
                </Button>
              )}
            </div>

            {/* Role Info Banner */}
            {!isSuperAdmin && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> You are logged in as a standard admin. Only Super Admins can add, edit, or delete admin users.
                </p>
              </div>
            )}

            {/* Add Admin Form */}
            {showAddAdmin && isSuperAdmin && (
              <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Create New Admin</h3>
                <form onSubmit={handleAddAdmin} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Username *</label>
                    <Input
                      type="text"
                      value={newAdmin.username}
                      onChange={(e) => setNewAdmin({...newAdmin, username: e.target.value})}
                      placeholder="Enter username"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Password *</label>
                    <div className="relative">
                      <Input
                        type={showNewAdminPassword ? "text" : "password"}
                        value={newAdmin.password}
                        onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                        placeholder="Enter password"
                        required
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewAdminPassword(!showNewAdminPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showNewAdminPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                    <Input
                      type="text"
                      value={newAdmin.full_name}
                      onChange={(e) => setNewAdmin({...newAdmin, full_name: e.target.value})}
                      placeholder="Enter full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <Input
                      type="email"
                      value={newAdmin.email}
                      onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                      placeholder="Enter email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                    <select
                      value={newAdmin.role}
                      onChange={(e) => setNewAdmin({...newAdmin, role: e.target.value})}
                      className="w-full rounded-md border border-slate-200 px-3 py-2 text-slate-700 bg-white"
                    >
                      <option value="admin">Admin (Standard)</option>
                      <option value="super_admin">Super Admin (Full Control)</option>
                    </select>
                  </div>
                  <div className="flex items-end gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={() => setShowAddAdmin(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-slate-700 hover:bg-slate-800">
                      Create Admin
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Admin Users List */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 bg-slate-50 border-b border-slate-200">
                <p className="text-sm text-slate-600">{adminUsers.length} admin user{adminUsers.length !== 1 ? 's' : ''}</p>
              </div>
              
              {adminUsers.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p>No admin users in database</p>
                  <p className="text-sm mt-1">Using default admin credentials</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {adminUsers.map((admin) => (
                    <div key={admin.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          admin.role === 'super_admin' ? 'bg-amber-100' : admin.is_active ? 'bg-emerald-100' : 'bg-slate-100'
                        }`}>
                          {admin.role === 'super_admin' ? (
                            <Crown className="w-5 h-5 text-amber-600" />
                          ) : (
                            <span className={`font-bold ${admin.is_active ? 'text-emerald-700' : 'text-slate-500'}`}>
                              {admin.username.substring(0, 2).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-slate-800">{admin.full_name || admin.username}</p>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              admin.role === 'super_admin' 
                                ? 'bg-amber-100 text-amber-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {admin.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                            </span>
                          </div>
                          <p className="text-sm text-slate-500">{admin.email || admin.username}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          admin.is_active 
                            ? 'bg-emerald-100 text-emerald-700' 
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {admin.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {isSuperAdmin && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditAdmin(admin)}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => toggleAdminStatus(admin.id, admin.is_active)}
                            >
                              {admin.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-700 border-amber-200 hover:bg-amber-50"
                              onClick={() => openResetPasswordModal(admin)}
                              title="Reset this admin's password"
                              data-testid={`admin-reset-pw-${admin.id}`}
                            >
                              <KeyRound className="w-4 h-4 mr-1" />
                              Reset Password
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => handleDeleteAdmin(admin.id, admin.username)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Reset Password Modal */}
            {resetPasswordAdmin && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="reset-password-modal">
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7">
                  <div className="flex items-start justify-between gap-4 mb-5">
                    <div>
                      <div className="w-11 h-11 rounded-full bg-amber-100 flex items-center justify-center mb-3">
                        <KeyRound className="w-5 h-5 text-amber-600" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-800">Reset Password</h3>
                      <p className="text-sm text-slate-500 mt-1">
                        For <span className="font-semibold text-slate-700">{resetPasswordAdmin.full_name || resetPasswordAdmin.username}</span>
                        <br />
                        <span className="text-xs text-slate-400">
                          {resetPasswordAdmin.email
                            ? `Email will be sent to ${resetPasswordAdmin.email}`
                            : 'No email on file — you will need to share the new password directly.'}
                        </span>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={closeResetPasswordModal}
                      className="text-slate-400 hover:text-slate-600"
                      aria-label="Close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {!resetPasswordResult ? (
                    <>
                      {/* Mode toggle */}
                      <div className="flex gap-2 mb-4">
                        <button
                          type="button"
                          onClick={() => setResetPasswordMode('generate')}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                            resetPasswordMode === 'generate'
                              ? 'border-amber-500 bg-amber-50 text-amber-700'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-amber-300'
                          }`}
                          data-testid="reset-pw-mode-generate"
                        >
                          Auto-generate
                        </button>
                        <button
                          type="button"
                          onClick={() => setResetPasswordMode('custom')}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                            resetPasswordMode === 'custom'
                              ? 'border-amber-500 bg-amber-50 text-amber-700'
                              : 'border-slate-200 bg-white text-slate-600 hover:border-amber-300'
                          }`}
                          data-testid="reset-pw-mode-custom"
                        >
                          Type custom
                        </button>
                      </div>

                      {resetPasswordMode === 'generate' ? (
                        <p className="text-sm text-slate-500 bg-slate-50 rounded-lg px-3 py-2.5 mb-4">
                          A secure 14-character password will be generated and emailed to the admin (if they have an email on file).
                        </p>
                      ) : (
                        <div className="mb-4">
                          <label className="block text-xs font-semibold text-slate-600 mb-1">New password (min 8 characters)</label>
                          <Input
                            type="text"
                            value={resetPasswordCustom}
                            onChange={(e) => setResetPasswordCustom(e.target.value)}
                            placeholder="Type the new password"
                            data-testid="reset-pw-custom-input"
                          />
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="flex-1"
                          onClick={closeResetPasswordModal}
                          disabled={isResettingPassword}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={submitResetPassword}
                          disabled={isResettingPassword}
                          className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                          data-testid="reset-pw-submit"
                        >
                          {isResettingPassword ? 'Resetting…' : 'Reset Password'}
                        </Button>
                      </div>
                    </>
                  ) : (
                    /* Result view — show generated password (if any) + status */
                    <div className="space-y-3">
                      {resetPasswordResult.email_sent ? (
                        <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2.5 text-sm text-green-800">
                          <CheckCircle className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                          A confirmation email with the new password has been sent.
                        </div>
                      ) : (
                        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-800">
                          <AlertCircle className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                          The email could not be delivered. Please share the password directly with the admin.
                        </div>
                      )}

                      {resetPasswordResult.new_password && (
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1">Generated password</label>
                          <div className="flex gap-2">
                            <Input
                              type="text"
                              value={resetPasswordResult.new_password}
                              readOnly
                              className="font-mono text-sm"
                              data-testid="reset-pw-generated-value"
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(resetPasswordResult.new_password);
                                toast.success('Password copied to clipboard');
                              }}
                              title="Copy to clipboard"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                          <p className="text-xs text-slate-400 mt-1.5">
                            This is the only time the password is shown — copy it now if you need to share it directly.
                          </p>
                        </div>
                      )}

                      <div className="pt-2">
                        <Button
                          type="button"
                          onClick={closeResetPasswordModal}
                          className="w-full bg-slate-700 hover:bg-slate-800 text-white"
                          data-testid="reset-pw-close"
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Edit Admin Modal */}
            {editingAdmin && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-slate-800">Edit Admin: {editingAdmin.username}</h3>
                    <button onClick={handleCancelEdit} className="text-slate-400 hover:text-slate-600">
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <form onSubmit={handleSaveEdit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                      <Input
                        type="text"
                        value={editForm.full_name}
                        onChange={(e) => setEditForm({...editForm, full_name: e.target.value})}
                        placeholder="Enter full name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                      <Input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                        placeholder="Enter email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                      <select
                        value={editForm.role}
                        onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                        className="w-full rounded-md border border-slate-200 px-3 py-2 text-slate-700 bg-white"
                      >
                        <option value="admin">Admin (Standard)</option>
                        <option value="super_admin">Super Admin (Full Control)</option>
                      </select>
                    </div>
                    <div className="flex gap-3 justify-end pt-2">
                      <Button type="button" variant="outline" onClick={handleCancelEdit}>
                        Cancel
                      </Button>
                      <Button type="submit" className="bg-slate-700 hover:bg-slate-800">
                        Save Changes
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Info Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> The default admin credentials (dcelc_admin) is always a Super Admin and will continue to work even if not listed here. 
                {isSuperAdmin ? ' Add additional admin users above to give other team members their own login credentials.' : ''}
              </p>
            </div>
          </div>
        ) : (
          <>
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <p className="text-3xl font-bold text-slate-800">{stats.total}</p>
            <p className="text-sm text-slate-500">Total {activeTab === 'hc4cc' ? 'Inquiries' : 'Applications'}</p>
          </div>
          {activeTab === 'hc4cc' ? (
            <>
              <div className="bg-teal-50 rounded-xl p-4 border border-teal-200">
                <p className="text-3xl font-bold text-teal-700">{stats.new}</p>
                <p className="text-sm text-teal-600">New</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <p className="text-3xl font-bold text-blue-700">{stats.in_progress}</p>
                <p className="text-sm text-blue-600">In Progress</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <p className="text-3xl font-bold text-green-700">{stats.resolved}</p>
                <p className="text-sm text-green-600">Resolved</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <p className="text-3xl font-bold text-slate-700">{currentRegistrations.filter(r => r.status === 'archived').length}</p>
                <p className="text-sm text-slate-600">Archived</p>
              </div>
            </>
          ) : (
            <>
              <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                <p className="text-3xl font-bold text-yellow-700">{stats.pending}</p>
                <p className="text-sm text-yellow-600">Pending</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                <p className="text-3xl font-bold text-blue-700">{stats.reviewing}</p>
                <p className="text-sm text-blue-600">Reviewing</p>
              </div>
              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                <p className="text-3xl font-bold text-green-700">{stats.accepted}</p>
                <p className="text-sm text-green-600">Accepted</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                <p className="text-3xl font-bold text-red-700">{stats.declined}</p>
                <p className="text-sm text-red-600">Declined</p>
              </div>
            </>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="text"
                placeholder={`Search by name, email, or ${activeTab === 'hc4cc' ? 'center' : 'organization'}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-slate-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-md border border-slate-200 px-3 py-2 text-slate-700 bg-white"
              >
                <option value="all">All Status</option>
                {activeTab === 'hc4cc' ? (
                  <>
                    <option value="new">New</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="archived">Archived</option>
                  </>
                ) : (
                  <>
                    <option value="pending">Pending</option>
                    <option value="reviewing">Reviewing</option>
                    <option value="accepted">Accepted</option>
                    <option value="waitlisted">Waitlisted</option>
                    <option value="declined">Declined</option>
                  </>
                )}
              </select>
            </div>
            <Button onClick={fetchAllRegistrations} variant="outline">
              Refresh
            </Button>
            {activeTab === 'hc4cc' && (
              <>
                <Button onClick={() => setShowReportModal(true)} className="bg-teal-500 hover:bg-teal-600 text-white">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Visual Report
                </Button>
                <Button onClick={exportHC4CCToCSV} variant="outline" className="text-teal-600 border-teal-200 hover:bg-teal-50">
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button onClick={generateHC4CCSummaryReport} variant="outline" className="text-teal-600 border-teal-200 hover:bg-teal-50">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Text Report
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Registrations List */}
        <div className="space-y-4">
          {filteredRegistrations.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-slate-200">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No {activeTab === 'hc4cc' ? 'inquiries' : 'registrations'} found</p>
            </div>
          ) : (
            filteredRegistrations.map((reg) => (
              <div key={reg.id} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                {/* Summary Row */}
                <div 
                  className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedId(expandedId === reg.id ? null : reg.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 ${activeTab === 'alaa' ? 'bg-amber-100' : activeTab === 'itci' ? 'bg-rose-100' : 'bg-teal-100'} rounded-full flex items-center justify-center`}>
                        <span className={`${activeTab === 'alaa' ? 'text-amber-700' : activeTab === 'itci' ? 'text-rose-700' : 'text-teal-700'} font-bold`}>
                          {activeTab === 'hc4cc' 
                            ? (reg.full_name?.split(' ').map(n => n[0]).join('') || '??')
                            : `${reg.first_name?.[0] || ''}${reg.last_name?.[0] || ''}`
                          }
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">
                          {activeTab === 'hc4cc' ? reg.full_name : `${reg.first_name} ${reg.last_name}`}
                        </h3>
                        <p className="text-sm text-slate-500">
                          {activeTab === 'alaa' ? reg.program_name : activeTab === 'itci' ? reg.organization_name : reg.center_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {getStatusBadge(reg.status)}
                      <span className="text-sm text-slate-400">
                        {new Date(reg.submitted_at).toLocaleDateString()}
                      </span>
                      {expandedId === reg.id ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedId === reg.id && (
                  <div className="border-t border-slate-200 p-6 bg-slate-50">
                    {activeTab === 'hc4cc' ? (
                      /* HC4CC Inquiry Details */
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Contact Info */}
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                            <Users className="w-4 h-4" /> Contact Information
                          </h4>
                          <div className="space-y-2 text-sm">
                            <p className="flex items-center gap-2 text-slate-600">
                              <Mail className="w-4 h-4 text-slate-400" /> {reg.email}
                            </p>
                            <p className="flex items-center gap-2 text-slate-600">
                              <Phone className="w-4 h-4 text-slate-400" /> {reg.phone}
                            </p>
                          </div>
                        </div>

                        {/* Employment Info */}
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                            <Home className="w-4 h-4" /> Employment
                          </h4>
                          <div className="space-y-2 text-sm">
                            <p className="text-slate-600"><strong>Center/Home:</strong> {reg.center_name}</p>
                            <p className="text-slate-600"><strong>Ward:</strong> {reg.ward}</p>
                            <p className="text-slate-600"><strong>Resides in:</strong> {reg.residence}</p>
                          </div>
                        </div>

                        {/* Insurance Info */}
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                            <Shield className="w-4 h-4" /> Insurance Details
                          </h4>
                          <div className="space-y-2 text-sm">
                            <p className="text-slate-600"><strong>Insurance Company:</strong> {reg.insurance_company}</p>
                            <p className="text-slate-600"><strong>Coverage Type:</strong> {reg.coverage_type}</p>
                            <p className="text-slate-600"><strong>Metal Level:</strong> {reg.metal_level}</p>
                          </div>
                        </div>

                        {/* Knowledge & Needs */}
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                            <HelpCircle className="w-4 h-4" /> Questions & Needs
                          </h4>
                          <div className="space-y-2 text-sm">
                            <p className="text-slate-600"><strong>Knows Free Services:</strong> {reg.knows_free_services}</p>
                            <p className="text-slate-600"><strong>Helpful Info:</strong> {reg.helpful_info?.join(', ') || 'None specified'}</p>
                            {reg.helpful_info_other && (
                              <p className="text-slate-600"><strong>Other:</strong> {reg.helpful_info_other}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* ALAA/ITCI Details */
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Contact Info */}
                      <div>
                        <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Users className="w-4 h-4" /> Contact Information
                        </h4>
                        <div className="space-y-2 text-sm">
                          <p className="flex items-center gap-2 text-slate-600">
                            <Mail className="w-4 h-4 text-slate-400" /> {reg.email}
                          </p>
                          <p className="flex items-center gap-2 text-slate-600">
                            <Phone className="w-4 h-4 text-slate-400" /> {reg.phone}
                          </p>
                        </div>
                      </div>

                      {/* Professional Info */}
                      <div>
                        <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <GraduationCap className="w-4 h-4" /> Professional Background
                        </h4>
                        <div className="space-y-2 text-sm">
                          {activeTab === 'alaa' ? (
                            <>
                              <p className="text-slate-600"><strong>Education:</strong> {reg.education_level}</p>
                              <p className="text-slate-600"><strong>Position:</strong> {reg.job_title}</p>
                              <p className="text-slate-600"><strong>Years:</strong> {reg.years_in_position}</p>
                            </>
                          ) : (
                            <>
                              <p className="text-slate-600"><strong>Role:</strong> {reg.current_role}</p>
                              <p className="text-slate-600"><strong>ECE Years:</strong> {reg.years_in_ece}</p>
                              <p className="text-slate-600"><strong>I/T Years:</strong> {reg.years_in_infant_toddler}</p>
                              <p className="text-slate-600"><strong>Coaching Exp:</strong> {reg.coaching_experience}</p>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Organization/Program Info */}
                      <div>
                        <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Building2 className="w-4 h-4" /> {activeTab === 'alaa' ? 'Program Details' : 'Organization Details'}
                        </h4>
                        <div className="space-y-2 text-sm">
                          {activeTab === 'alaa' ? (
                            <>
                              <p className="flex items-center gap-2 text-slate-600">
                                <MapPin className="w-4 h-4 text-slate-400" /> {reg.program_address}
                              </p>
                              <p className="text-slate-600"><strong>Faculty:</strong> {reg.number_of_faculty}</p>
                              <p className="text-slate-600"><strong>Capacity:</strong> {reg.license_capacity} children</p>
                              <p className="text-slate-600"><strong>Classrooms:</strong> {reg.number_of_classrooms}</p>
                              <p className="text-slate-600"><strong>Ages:</strong> {reg.age_groups_served?.join(', ')}</p>
                            </>
                          ) : (
                            <>
                              <p className="text-slate-600"><strong>Organization:</strong> {reg.organization_name}</p>
                              <p className="text-slate-600"><strong>Type:</strong> {reg.organization_type}</p>
                              <p className="flex items-center gap-2 text-slate-600">
                                <MapPin className="w-4 h-4 text-slate-400" /> {reg.organization_address}
                              </p>
                              <p className="text-slate-600"><strong>Programs Supported:</strong> {reg.programs_supported}</p>
                              <p className="text-slate-600"><strong>Age Focus:</strong> {reg.age_groups_focus?.join(', ')}</p>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Certifications/Expertise (ITCI only) */}
                      {activeTab === 'itci' && (
                        <div>
                          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                            <Star className="w-4 h-4" /> Certifications & Expertise
                          </h4>
                          <div className="space-y-2 text-sm">
                            <p className="text-slate-600"><strong>Certifications:</strong> {reg.certifications?.join(', ') || 'None listed'}</p>
                            <p className="text-slate-600"><strong>Areas:</strong> {reg.areas_of_expertise?.join(', ') || 'None listed'}</p>
                          </div>
                        </div>
                      )}

                      {/* Essays */}
                      <div className="md:col-span-2">
                        <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4" /> Application Essays
                        </h4>
                        <div className="space-y-4">
                          {activeTab === 'alaa' ? (
                            <>
                              <div className="bg-white p-4 rounded-lg border border-slate-200">
                                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Short Bio</p>
                                <p className="text-sm text-slate-700">{reg.short_bio}</p>
                              </div>
                              <div className="bg-white p-4 rounded-lg border border-slate-200">
                                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Why Join Emerging Leaders</p>
                                <p className="text-sm text-slate-700">{reg.why_join}</p>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="bg-white p-4 rounded-lg border border-slate-200">
                                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Professional Goals</p>
                                <p className="text-sm text-slate-700">{reg.professional_goals}</p>
                              </div>
                              <div className="bg-white p-4 rounded-lg border border-slate-200">
                                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Why Join the Institute</p>
                                <p className="text-sm text-slate-700">{reg.why_join}</p>
                              </div>
                              {reg.supervisor_name && (
                                <div className="bg-white p-4 rounded-lg border border-slate-200">
                                  <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Supervisor</p>
                                  <p className="text-sm text-slate-700">{reg.supervisor_name} - {reg.supervisor_email}</p>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-6 pt-6 border-t border-slate-200 flex flex-wrap items-center gap-3">
                      <span className="text-sm text-slate-500 mr-2">Update Status:</span>
                      {(activeTab === 'hc4cc' 
                        ? ['new', 'in_progress', 'resolved', 'archived']
                        : ['pending', 'reviewing', 'accepted', 'waitlisted', 'declined']
                      ).map((status) => (
                        <Button
                          key={status}
                          onClick={() => updateStatus(reg.id, status, activeTab)}
                          variant={reg.status === status ? 'default' : 'outline'}
                          size="sm"
                          className={reg.status === status ? (activeTab === 'alaa' ? 'bg-amber-500 hover:bg-amber-600' : activeTab === 'itci' ? 'bg-rose-500 hover:bg-rose-600' : 'bg-teal-500 hover:bg-teal-600') : ''}
                        >
                          {status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                        </Button>
                      ))}
                      <Button
                        onClick={() => deleteRegistration(reg.id, activeTab)}
                        variant="outline"
                        size="sm"
                        className="ml-auto text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
          </>
        )}
      </main>

      {/* HC4CC Report Modal */}
      <HC4CCReportModal 
        isOpen={showReportModal} 
        onClose={() => setShowReportModal(false)} 
        inquiries={hc4ccInquiries}
      />
    </div>
  );
};

export default AdminPage;
